import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios, { getActiveOrgId } from "../lib/api";
import {
  Plug,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Settings,
  ChevronDown,
  Upload,
  FileJson,
  Clock,
  ToggleLeft,
  ToggleRight,
  Package,
  ShoppingCart,
  RefreshCw,
  Users,
  History,
  Filter,
  Shield,
  Edit
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";

const platformConfig = {
  meta_ads: {
    name: "Meta Ads",
    color: "bg-blue-600",
    description: "Facebook e Instagram Ads - Importa datos de todas tus campañas.",
  },
  awin: {
    name: "Awin",
    color: "bg-purple-600",
    description: "Red de afiliación - Importa comisiones y conversiones de afiliados.",
  },
  tiktok_ads: {
    name: "TikTok Ads",
    color: "bg-[#0A0A0A]",
    description: "Conecta TikTok Marketing API para métricas de publicidad.",
  }
};

// Meta Ads Configuration Form
const MetaAdsConfigForm = ({ onSuccess, onClose, organizationId }) => {
  const [adAccountId, setAdAccountId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [error, setError] = useState("");

  const handleOAuthConnect = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      toast.error("Sesión expirada. Vuelve a iniciar sesión.");
      return;
    }
    try {
      const orgParam = organizationId ? `?organization_id=${organizationId}` : "";
      const res = await axios.get(`${API}/meta-ads/oauth/install${orgParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const target = res.data?.auth_url;
      if (!target) {
        toast.error("No se pudo iniciar el flujo OAuth de Meta");
        return;
      }
      window.location.href = target;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Error al iniciar Meta OAuth";
      toast.error(msg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/meta-ads/configure`,
        {
          ad_account_id: adAccountId.replace(/\s/g, ""),
          access_token: accessToken.trim()
        },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      toast.success("Meta Ads conectado correctamente");
      onSuccess();
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al configurar Meta Ads";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* One-click OAuth */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-[#0A0A0A] rounded-lg p-5 shadow">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <i className="fab fa-facebook text-xl" />
          </div>
          <div className="flex-1">
            <p className="font-semibold mb-1">Conexión automática (recomendada)</p>
            <p className="text-sm text-[#0A0A0A]/85 mb-4">
              Inicia sesión con Facebook y autoriza el acceso a tus cuentas publicitarias.
              Sin copiar tokens ni IDs. El token se renueva solo cada 60 días.
            </p>
            <Button
              type="button"
              onClick={handleOAuthConnect}
              className="bg-white text-blue-700 hover:bg-blue-50"
              data-testid="meta-ads-oauth-connect-button"
            >
              <i className="fab fa-facebook mr-2" />
              Conectar con Facebook
            </Button>
          </div>
        </div>
      </div>

      {/* Advanced — manual token (fallback for System Users, etc.) */}
      <button
        type="button"
        onClick={() => setShowManual((v) => !v)}
        className="text-xs text-[#AAAAAA] hover:text-slate-700 underline"
        data-testid="meta-ads-toggle-manual"
      >
        {showManual ? "Ocultar configuración manual" : "¿Prefieres pegar un token manualmente? (avanzado)"}
      </button>

      {showManual && (
        <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
          <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
            <p className="font-medium mb-2">Pasos para configurar Meta Ads manualmente:</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700">
              <li>Ve a <a href="https://business.facebook.com" target="_blank" rel="noopener" className="underline">Meta Business Suite</a> → Configuración → Cuentas publicitarias</li>
              <li>Copia tu Ad Account ID</li>
              <li>Ve a <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener" className="underline">Graph API Explorer</a></li>
              <li>Genera un token con permisos: ads_read, ads_management</li>
              <li>Extiende el token a Long-lived (60 días)</li>
            </ol>
          </div>

          <div className="space-y-2">
            <Label>Ad Account ID</Label>
            <Input
              data-testid="meta-ads-account-input"
              type="text"
              placeholder="264146257564510 o act_264146257564510"
              value={adAccountId}
              onChange={(e) => setAdAccountId(e.target.value)}
              required
              className="font-mono"
            />
            <p className="text-xs text-[#AAAAAA]">
              Lo encuentras en Meta Business Suite → Configuración → Cuentas publicitarias
            </p>
          </div>

          <div className="space-y-2">
            <Label>Access Token</Label>
            <Textarea
              data-testid="meta-ads-access-token-input"
              placeholder="EAAcg977HoyMBO..."
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              rows={3}
              className="font-mono text-xs"
              required
            />
            <p className="text-xs text-[#AAAAAA]">
              Token de larga duración (60 días) con permisos ads_read
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1" data-testid="meta-ads-error">
              <AlertCircle className="w-4 h-4" /> {error}
            </p>
          )}

          <Button
            type="submit"
            className="w-full bg-[#C8192C] hover:bg-[#A0141F] text-white"
            disabled={loading}
            data-testid="meta-ads-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verificando...
              </>
            ) : (
              "Conectar Meta Ads"
            )}
          </Button>
        </form>
      )}
    </div>
  );
};

// Awin Configuration Form
const AwinConfigForm = ({ onSuccess, onClose }) => {
  const [publisherId, setPublisherId] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      await axios.post(
        `${API}/awin/configure`,
        { 
          publisher_id: publisherId.trim(),
          api_token: apiToken.trim()
        },
        {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const syncResponse = await axios.post(
        `${API}/awin/sync-transactions`,
        {},
        {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const synced = syncResponse.data?.records_synced ?? syncResponse.data?.transactions_synced ?? 0;
      toast.success(`Awin conectado y sincronizado (${synced} registros)`);
      onSuccess();
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al configurar Awin";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-purple-50 p-4 rounded-lg text-sm text-purple-800">
        <p className="font-medium mb-2">Configuración de Awin:</p>
        <ol className="list-decimal list-inside space-y-1 text-purple-700">
          <li>Ve a Account → API Credentials en Awin</li>
          <li>Genera un API Token</li>
          <li>Copia tu Publisher ID y el token</li>
        </ol>
      </div>

      <div className="space-y-2">
        <Label>Account ID de Awin</Label>
        <Input
          data-testid="awin-publisher-id-input"
          type="text"
          placeholder="106353"
          value={publisherId}
          onChange={(e) => setPublisherId(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label>API Token</Label>
        <Textarea
          data-testid="awin-api-token-input"
          placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
          value={apiToken}
          onChange={(e) => setApiToken(e.target.value)}
          required
          rows={3}
          className="font-mono text-xs"
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1" data-testid="awin-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </p>
      )}

      <Button
        type="submit"
        className="w-full bg-[#C8192C] hover:bg-[#A0141F] text-white"
        disabled={loading}
        data-testid="awin-submit-button"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Verificando...
          </>
        ) : (
          "Conectar Awin"
        )}
      </Button>
    </form>
  );
};

// Generic placeholder form for TikTok
const PlaceholderForm = ({ platform }) => {
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 p-4 rounded-lg text-sm text-amber-800">
        <p className="font-medium mb-2">Próximamente disponible</p>
        <p className="text-amber-700">
          La integración con TikTok Ads estará disponible pronto.
          Por ahora, puedes usar la conexión de Google Ads + GA4 y Meta Ads para medir tus campañas.
        </p>
      </div>
    </div>
  );
};

// Meta Ads: shows selected ad account, token expiry, and lets the user switch account
const MetaAdsAccountPanel = ({ connectionInfo, organizationId, onRefresh }) => {
  const [switching, setSwitching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const available = Array.isArray(connectionInfo?.available_accounts)
    ? connectionInfo.available_accounts
    : [];
  const currentId = connectionInfo?.ad_account_id;
  const authMethod = connectionInfo?.auth_method;
  const daysLeft = connectionInfo?.days_until_expiry;
  const needsRefresh = connectionInfo?.needs_refresh;

  const formatExpiry = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("es-ES");
    } catch {
      return iso;
    }
  };

  const handleSwitch = async (e) => {
    const newId = e.target.value;
    if (!newId || newId === currentId) return;
    setSwitching(true);
    try {
      const token = localStorage.getItem("auth_token");
      const orgParam = organizationId ? `?organization_id=${organizationId}` : "";
      await axios.post(
        `${API}/meta-ads/select-account${orgParam}`,
        { ad_account_id: newId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Cuenta publicitaria cambiada");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar la cuenta");
    } finally {
      setSwitching(false);
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem("auth_token");
      const orgParam = organizationId ? `?organization_id=${organizationId}` : "";
      await axios.post(
        `${API}/meta-ads/refresh-token${orgParam}`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Token Meta renovado");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo renovar el token");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-2 mt-2 pt-2 border-t border-slate-200" data-testid="meta-ads-account-panel">
      <p className="text-slate-600">
        <span className="font-medium">Ad Account ID:</span>{" "}
        <span className="font-mono text-xs">{currentId}</span>
      </p>
      {connectionInfo?.account_name && (
        <p className="text-slate-600">
          <span className="font-medium">Cuenta:</span> {connectionInfo.account_name}
        </p>
      )}
      <p className="text-slate-600">
        <span className="font-medium">Autenticación:</span>{" "}
        {authMethod === "oauth" ? (
          <Badge variant="secondary" className="bg-green-100 text-green-700 ml-1">OAuth</Badge>
        ) : (
          <Badge variant="secondary" className="bg-amber-100 text-amber-700 ml-1">Token manual</Badge>
        )}
      </p>

      {authMethod === "oauth" && (
        <p className="text-slate-600">
          <span className="font-medium">Token expira:</span>{" "}
          {formatExpiry(connectionInfo?.token_expires_at)}{" "}
          {typeof daysLeft === "number" && (
            <span className={needsRefresh ? "text-amber-600 font-medium" : "text-[#AAAAAA]"}>
              ({daysLeft} {daysLeft === 1 ? "día" : "días"})
            </span>
          )}
          {needsRefresh && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-2 h-6 px-2 text-xs"
              onClick={handleManualRefresh}
              disabled={refreshing}
              data-testid="meta-ads-refresh-token-btn"
            >
              {refreshing ? "Renovando..." : "Renovar ahora"}
            </Button>
          )}
        </p>
      )}

      {available.length > 1 && (
        <div className="pt-1">
          <Label className="text-xs font-medium text-slate-700">
            Cambiar cuenta publicitaria ({available.length} disponibles)
          </Label>
          <select
            value={currentId || ""}
            onChange={handleSwitch}
            disabled={switching}
            data-testid="meta-ads-account-selector"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
          >
            {available.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.name || acc.id} {acc.currency ? `· ${acc.currency}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

const IntegrationCard = ({
  platform,
  config,
  connected,
  connectionInfo,
  onRefresh,
  onAwinSync,
  syncingAwin,
  organizationId,
  checkError = false,   // true when status API call failed (transient error)
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString("es-ES");
    } catch {
      return value;
    }
  };

  const handleDisconnect = async () => {
    if (!confirm(`¿Estás seguro de desconectar ${config.name}?`)) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      // We don't actually have a disconnect endpoint, so just show a message
      toast.info("Para desconectar, elimina la configuración de la base de datos");
    } catch (error) {
      toast.error("Error al desconectar");
    }
  };

  const renderConfigForm = () => {
    switch (platform) {
      case "meta_ads":
        return <MetaAdsConfigForm onSuccess={onRefresh} onClose={() => setDialogOpen(false)} organizationId={organizationId} />;
      case "awin":
        return <AwinConfigForm onSuccess={onRefresh} onClose={() => setDialogOpen(false)} />;
      default:
        return <PlaceholderForm platform={platform} />;
    }
  };

  return (
    <Card className="bg-white border border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 ${config.color} rounded-xl flex items-center justify-center`}>
              <Plug className="w-6 h-6 text-[#0A0A0A]" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">{config.name}</h3>
              <p className="text-sm text-[#AAAAAA] mt-1">{config.description}</p>
            </div>
          </div>
          
          {checkError ? (
            <Badge variant="secondary" className="bg-amber-50 text-amber-700 border border-amber-200" data-testid={`${platform}-check-error-badge`}>
              <AlertCircle className="w-3 h-3 mr-1" /> No se pudo comprobar
            </Badge>
          ) : connected ? (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Conectado
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-slate-100 text-slate-600">
              <XCircle className="w-3 h-3 mr-1" /> No conectado
            </Badge>
          )}
        </div>

        {connected && connectionInfo && (
          <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
            {connectionInfo.property_id && (
              <p className="text-slate-600">
                <span className="font-medium">Property ID:</span> {connectionInfo.property_id}
              </p>
            )}
            {connectionInfo.customer_id && (
              <p className="text-slate-600">
                <span className="font-medium">Customer ID:</span> {connectionInfo.customer_id}
              </p>
            )}
            {connectionInfo.auth_type && (
              <p className="text-slate-600">
                <span className="font-medium">Autenticación:</span> {connectionInfo.auth_type === "service_account" ? "Service Account" : "OAuth"}
              </p>
            )}
            {platform === "awin" && connectionInfo.publisher_id && (
              <p className="text-slate-600" data-testid="awin-account-id-info">
                <span className="font-medium">Account ID:</span> {connectionInfo.publisher_id}
              </p>
            )}
            {platform === "awin" && connectionInfo.account_type && (
              <p className="text-slate-600" data-testid="awin-account-type-info">
                <span className="font-medium">Tipo:</span> {connectionInfo.account_type}
              </p>
            )}
            {platform === "awin" && (
              <p className="text-slate-600" data-testid="awin-last-sync-info">
                <span className="font-medium">Última sync:</span> {formatDateTime(connectionInfo.last_sync_date)}
              </p>
            )}
            {platform === "awin" && (
              <p className="text-slate-600" data-testid="awin-sync-summary-info">
                <span className="font-medium">Modo:</span> {connectionInfo.last_sync_type || "-"} · <span className="font-medium">Registros:</span> {connectionInfo.records_synced ?? 0}
              </p>
            )}
            {platform === "awin" && connectionInfo.last_error && (
              <p className="text-red-600" data-testid="awin-last-error-info">
                <span className="font-medium">Último error:</span> {connectionInfo.last_error}
              </p>
            )}
            {platform === "meta_ads" && (
              <MetaAdsAccountPanel
                connectionInfo={connectionInfo}
                organizationId={organizationId}
                onRefresh={onRefresh}
              />
            )}
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          {connected ? (
            <>
              {platform === "awin" && (
                <Button
                  variant="outline"
                  className="flex-1 border-slate-200 text-slate-700"
                  onClick={onAwinSync}
                  disabled={syncingAwin}
                  data-testid="awin-sync-button"
                >
                  {syncingAwin ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sincronizar Awin
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                className="flex-1 border-slate-200 text-slate-700"
                onClick={handleDisconnect}
                data-testid={`disconnect-${platform}-btn`}
              >
                Desconectar
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="icon" className="border-slate-200" data-testid={`open-config-${platform}-btn`}>
                    <Settings className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Configurar {config.name}</DialogTitle>
                    <DialogDescription>
                      Actualiza la configuración de tu integración
                    </DialogDescription>
                  </DialogHeader>
                  {renderConfigForm()}
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="flex-1 bg-[#C8192C] hover:bg-[#A0141F] text-white"
                  data-testid={`connect-${platform}-btn`}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configurar conexión
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Conectar {config.name}</DialogTitle>
                  <DialogDescription>
                    Configura la conexión con {config.name}
                  </DialogDescription>
                </DialogHeader>
                {renderConfigForm()}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Integrations = () => {
  const [searchParams] = useSearchParams();
  const { orgRole } = useAuth();
  const isOwner = !orgRole || orgRole === "owner";
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState({
    meta_ads: { connected: false },
    awin: { connected: false },
    tiktok_ads: { connected: false }
  });
  // Track which integrations had a fetch error (API call failed, distinct from "disconnected")
  const [statusErrors, setStatusErrors] = useState({});
  const [googleStatus, setGoogleStatus] = useState({
    connected: false,
    status: "disconnected",
    has_ads_customer_id: false,
    has_ga4_property_id: false,
    google_ads_customer_id: null,
    ga4_property_id: null,
    last_updated: null
  });
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [ga4Properties, setGa4Properties] = useState([]);
  const [selectedGoogleAdsAccount, setSelectedGoogleAdsAccount] = useState("");
  const [selectedGa4Property, setSelectedGa4Property] = useState("");
  const [loadingGoogleResources, setLoadingGoogleResources] = useState(false);
  const [savingGoogleSelection, setSavingGoogleSelection] = useState(false);
  const [disconnectingGoogle, setDisconnectingGoogle] = useState(false);
  const [syncingAwin, setSyncingAwin] = useState(false);
  const [prestashopStatus, setPrestashopStatus] = useState(null);
  const [productCostsStatus, setProductCostsStatus] = useState(null);
  const [ordersSyncStatus, setOrdersSyncStatus] = useState(null);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [backfillingCustomers, setBackfillingCustomers] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resettingAccount, setResettingAccount] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [autoSyncNew, setAutoSyncNew] = useState({ configs: {}, planMinInterval: 1440, syncOptions: [1440], syncLabels: {} });
  const [savingAutoSync, setSavingAutoSync] = useState(false);
  const [autoSyncLogs, setAutoSyncLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // PrestaShop settings state
  const [psOrderStates, setPsOrderStates] = useState([]);
  const [psSelectedStates, setPsSelectedStates] = useState([]);
  const [psCancelledStates, setPsCancelledStates] = useState([]);
  const [psDefaultMargin, setPsDefaultMargin] = useState(30);
  const [psSavingSpecialStates, setPsSavingSpecialStates] = useState(false);
  const [psSavingMargin, setPsSavingMargin] = useState(false);
  const [psConfigOpen, setPsConfigOpen] = useState(false);
  const [psConfigForm, setPsConfigForm] = useState({ shop_url: "", api_key: "", default_margin_percentage: 30, shop_id: "" });
  const [psConfigLoading, setPsConfigLoading] = useState(false);
  const [psSettingsOpen, setPsSettingsOpen] = useState(false);

  // Shopify state
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [shopifyRecalculating, setShopifyRecalculating] = useState(false);
  const [shopifyBackfilling, setShopifyBackfilling] = useState(false);
  const [shopifyConnecting, setShopifyConnecting] = useState(false);
  const [shopifyDomain, setShopifyDomain] = useState("");
  const [shopifyToken, setShopifyToken] = useState("");
  const [shopifyApiVersion, setShopifyApiVersion] = useState("2025-01");
  const [shopifyShowForm, setShopifyShowForm] = useState(false);
  const [shopifySyncingProducts, setShopifySyncingProducts] = useState(false);
  const [shopifySyncingOrders, setShopifySyncingOrders] = useState(false);
  const [shopifyImportModal, setShopifyImportModal] = useState(false);
  const [shopifyManualMode, setShopifyManualMode] = useState(false);
  const [shopifyManualToken, setShopifyManualToken] = useState("");
  // Store-agnostic: default margin per integration
  const [shopifyDefaultMargin, setShopifyDefaultMargin] = useState(30);
  const [shopifySavingMargin, setShopifySavingMargin] = useState(false);

  // Repair all costs
  const [repairingAllCosts, setRepairingAllCosts] = useState(false);
  const [repairAllCostsStatus, setRepairAllCostsStatus] = useState(null);

  // Sales channels filter (Shopify-only)
  const [shopifyChannels, setShopifyChannels] = useState([]);
  const [shopifyActiveChannels, setShopifyActiveChannels] = useState([]);
  const [shopifyChannelsLoading, setShopifyChannelsLoading] = useState(false);
  const [shopifyChannelsSaving, setShopifyChannelsSaving] = useState(false);
  const [channelBackfillRunning, setChannelBackfillRunning] = useState(false);
  const [channelBackfillStatus, setChannelBackfillStatus] = useState(null);

  useEffect(() => {
    fetchStatus();
    const token = localStorage.getItem("auth_token");
    fetchRepairAllCostsStatus(token);
  }, []);

  // Fetch sales channels once Shopify is confirmed connected
  useEffect(() => {
    if (shopifyStatus?.configured) {
      fetchShopifyChannels();
    }
  }, [shopifyStatus?.configured]);

  useEffect(() => {
    if (prestashopStatus?.configured) {
      fetchPsOrderStates();
    }
  }, [prestashopStatus?.configured]);

  // Handle Shopify OAuth callback redirect (?shopify=connected / error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopifyResult = params.get("shopify");
    if (shopifyResult === "connected") {
      toast.success("Shopify conectado correctamente");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (shopifyResult === "error") {
      const reason = params.get("reason") || "unknown";
      const messages = {
        invalid_state: "Error de seguridad CSRF. Inténtalo de nuevo.",
        state_expired: "La sesión de OAuth expiró. Inténtalo de nuevo.",
        token_exchange_failed: "No se pudo intercambiar el token con Shopify. Comprueba la app.",
        empty_token: "Shopify devolvió un token vacío.",
      };
      toast.error(messages[reason] || `Error OAuth: ${reason}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const googleParam = searchParams.get("google");
    const reason = searchParams.get("reason");

    if (!googleParam) return;

    if (googleParam === "connected") {
      toast.success("Google conectado correctamente");
      fetchStatus();
      return;
    }

    if (googleParam === "error") {
      const friendlyGoogle = {
        not_authenticated: "Sesión expirada. Inicia sesión de nuevo y reintenta conectar Google.",
        invalid_state: "Token de seguridad caducado. Reintenta la conexión.",
        expired_state: "El proceso tardó demasiado. Reintenta conectar Google.",
        missing_code: "Google no devolvió el código de autorización.",
        token_exchange_failed: "Fallo al intercambiar el código con Google.",
      };
      toast.error(friendlyGoogle[reason] || `Error al conectar Google${reason ? `: ${reason}` : ""}`);
    }
  }, [searchParams]);

  // Handle Meta OAuth callback query params
  useEffect(() => {
    const metaParam = searchParams.get("meta_ads");
    const reason = searchParams.get("reason");
    const accounts = searchParams.get("accounts");
    if (!metaParam) return;

    if (metaParam === "connected") {
      const extra = accounts && Number(accounts) > 1 ? ` (${accounts} cuentas disponibles)` : "";
      toast.success(`Meta Ads conectado correctamente${extra}`);
      fetchStatus();
    } else if (metaParam === "error") {
      const friendly = {
        token_exchange_failed: "Fallo al intercambiar el código con Meta.",
        token_extend_failed: "Fallo al extender el token a larga duración.",
        adaccounts_fetch_failed: "No se pudieron obtener tus cuentas publicitarias.",
        no_ad_accounts: "No se encontraron cuentas publicitarias en tu Meta.",
        network: "Problema de red al hablar con Meta. Reintenta.",
        unexpected: "Error inesperado durante el OAuth.",
      };
      toast.error(friendly[reason] || `Error Meta OAuth: ${reason || "desconocido"}`);
    }
    // Clear params from URL so refreshing doesn't re-show the toast
    window.history.replaceState({}, "", window.location.pathname);
  }, [searchParams]);

  const fetchGoogleResources = async (token, orgId) => {
    if (!orgId) return false;

    setLoadingGoogleResources(true);
    try {
      const [adsResponse, ga4Response] = await Promise.all([
        axios.get(`${API}/integrations/google/ads/accounts`, {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }),
        axios.get(`${API}/integrations/google/ga4/properties`, {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
      ]);

      setGoogleAdsAccounts(adsResponse.data.customers || []);
      setGa4Properties(ga4Response.data.properties || []);
      return true;
    } catch (error) {
      toast.error("No se pudieron cargar las cuentas de Google");
      return false;
    } finally {
      setLoadingGoogleResources(false);
    }
  };

  const fetchPsOrderStates = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [statesRes, specialRes, configRes] = await Promise.all([
        axios.get(`${API}/prestashop/order-states`, { withCredentials: true, headers }),
        axios.get(`${API}/prestashop/special-states`, { withCredentials: true, headers }).catch(() => ({ data: { cancelled_order_states: [] } })),
        axios.get(`${API}/prestashop/config`, { withCredentials: true, headers }).catch(() => null),
      ]);
      setPsOrderStates(statesRes.data.states || []);
      setPsSelectedStates(statesRes.data.selected_states || []);
      setPsCancelledStates(specialRes.data.cancelled_order_states || []);
      if (configRes?.data?.configured) {
        setPsDefaultMargin(configRes.data.default_margin_percentage || 30);
      }
    } catch (error) {
      console.error("Error fetching PS order states:", error);
    }
  };

  const fetchStatus = async () => {
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const orgId = getActiveOrgId();

    try {
      // Launch all status calls in parallel — none blocks the others.
      // We use Promise.allSettled so a single slow/failed call never delays the rest.
      const [
        googleResult,
        metaResult,
        psResult,
        productCostsResult,
        ordersStatusResult,
        autoSyncResult,
        awinResult,
        shopifyResult,
      ] = await Promise.allSettled([
        axios.get(`${API}/integrations/google/status`, { params: { organization_id: orgId }, withCredentials: true, headers }),
        axios.get(`${API}/meta-ads/status`, { withCredentials: true, headers }),
        axios.get(`${API}/prestashop/status`, { withCredentials: true, headers }),
        axios.get(`${API}/prestashop/product-costs-status`, { withCredentials: true, headers }),
        axios.get(`${API}/prestashop/orders-sync-status`, { withCredentials: true, headers }),
        axios.get(`${API}/auto-sync/config`, { params: { organization_id: orgId }, withCredentials: true, headers }),
        axios.get(`${API}/awin/status`, { params: { organization_id: orgId }, withCredentials: true, headers }),
        axios.get(`${API}/shopify/status`, { withCredentials: true, headers }),
      ]);

      // Track which integrations had API errors (distinct from "not configured")
      const newErrors = {};

      // Google: on failure keep current state unchanged, set error flag
      if (googleResult.status === "fulfilled") {
        const googleData = googleResult.value.data;
        setGoogleStatus(googleData);
        setSelectedGoogleAdsAccount(googleData.google_ads_customer_id || "");
        setSelectedGa4Property(googleData.ga4_property_id || "");
        if (googleData.connected && googleData.auth_type === "oauth") {
          fetchGoogleResources(token, orgId);
        }
      } else {
        newErrors.google = true;
        console.warn("Google status check failed (keeping current state):", googleResult.reason?.message);
      }

      // Meta
      if (metaResult.status === "fulfilled") {
        const d = metaResult.value.data;
        setConnectionStatus(prev => ({
          ...prev,
          meta_ads: {
            connected: d.configured,
            ad_account_id: d.ad_account_id,
            account_name: d.account_name,
            auth_method: d.auth_method,
            available_accounts: d.available_accounts || [],
            token_expires_at: d.token_expires_at,
            days_until_expiry: d.days_until_expiry,
            needs_refresh: d.needs_refresh,
          }
        }));
      } else {
        newErrors.meta_ads = true;
      }

      // PrestaShop status (now from DB, not live)
      if (psResult.status === "fulfilled") {
        setPrestashopStatus(psResult.value.data);
      } else {
        newErrors.prestashop = true;
      }

      // Product costs
      if (productCostsResult.status === "fulfilled") {
        setProductCostsStatus(productCostsResult.value.data);
      } else {
        newErrors.product_costs = true;
      }

      // Orders sync
      if (ordersStatusResult.status === "fulfilled") {
        const d = ordersStatusResult.value.data;
        setOrdersSyncStatus(d);
        setBackfillingCustomers(d?.backfill_customers?.status === "running");
      } else {
        newErrors.orders_sync = true;
      }

      // Auto-sync config
      if (autoSyncResult.status === "fulfilled") {
        const d = autoSyncResult.value.data;
        setAutoSyncNew({
          configs: d.configs || {},
          planMinInterval: d.plan_min_interval ?? 1440,
          syncOptions: d.sync_options || [1440],
          syncLabels: d.sync_labels || {},
        });
      } else {
        newErrors.auto_sync = true;
      }

      // Awin
      if (awinResult.status === "fulfilled") {
        const d = awinResult.value.data;
        setConnectionStatus(prev => ({
          ...prev,
          awin: {
            connected: d.configured,
            publisher_id: d.publisher_id,
            account_type: d.account_type,
            account_name: d.account_name,
            last_sync_date: d.last_sync_date,
            last_sync_type: d.last_sync_type,
            records_synced: d.records_synced,
            last_error: d.last_error,
          },
          tiktok_ads: { connected: false },
        }));
      } else {
        newErrors.awin = true;
      }

      // Shopify
      if (shopifyResult.status === "fulfilled") {
        const d = shopifyResult.value.data;
        setShopifyStatus(d);
        if (d?.default_margin_percentage !== undefined) {
          setShopifyDefaultMargin(d.default_margin_percentage);
        }
      } else {
        newErrors.shopify = true;
      }

      setStatusErrors(newErrors);

      // Auto-sync logs — fire-and-forget (doesn't block the page)
      axios.get(`${API}/auto-sync/logs?limit=10`, {
        params: { organization_id: orgId }, withCredentials: true, headers
      }).then(r => setAutoSyncLogs(r.data.logs || [])).catch(() => {});

    } catch (error) {
      console.error("Error fetching status:", error);
    } finally {
      setLoading(false);
    }
  };

  const summaryPlatforms = [
    { key: "google", color: "bg-blue-500", connected: googleStatus.connected },
    ...Object.entries(platformConfig).map(([key, config]) => ({
      key,
      color: config.color,
      connected: connectionStatus[key]?.connected
    }))
  ];

  const connectedCount = summaryPlatforms.filter((platform) => platform.connected).length;
  const isProductsSyncing = syncingProducts || productCostsStatus?.status === 'syncing';
  const isOrdersSyncing = syncingOrders || ordersSyncStatus?.status === 'syncing';
  const isPrestashopSyncing = isProductsSyncing || isOrdersSyncing;
  const backfillStatus = ordersSyncStatus?.backfill_customers?.status;
  const backfillOffset = ordersSyncStatus?.backfill_customers?.current_offset || 0;
  const canResumeBackfill = !backfillingCustomers && (backfillStatus === "failed" || backfillStatus === "cancelled") && backfillOffset > 0;

  const handleGoogleConnect = () => {
    const orgId = getActiveOrgId();
    if (!orgId) {
      toast.error("No se pudo determinar la organización activa");
      return;
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Pass auth token in URL to survive cross-domain navigation (browser drops
    // cookie when backend is on different domain than frontend).
    const authToken = localStorage.getItem("auth_token") || "";
    const params = new URLSearchParams({ organization_id: orgId });
    if (authToken) params.set("token", authToken);
    window.location.href = `${API}/auth/google/start?${params.toString()}`;
  };

  const handleGoogleDisconnect = async () => {
    const orgId = getActiveOrgId();
    if (!orgId) {
      toast.error("No se pudo determinar la organización activa");
      return;
    }

    setDisconnectingGoogle(true);
    const token = localStorage.getItem("auth_token");

    try {
      await axios.post(
        `${API}/integrations/google/disconnect`,
        {},
        {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Google desconectado");
      await fetchStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al desconectar Google";
      toast.error(errorMsg);
    } finally {
      setDisconnectingGoogle(false);
    }
  };

  const handleGoogleSaveSelection = async () => {
    const orgId = getActiveOrgId();
    if (!orgId) {
      toast.error("No se pudo determinar la organización activa");
      return;
    }

    if (!selectedGoogleAdsAccount || !selectedGa4Property) {
      toast.error("Selecciona cuenta de Ads y propiedad GA4");
      return;
    }

    setSavingGoogleSelection(true);
    const token = localStorage.getItem("auth_token");

    try {
      await axios.post(
        `${API}/integrations/google/select`,
        {
          google_ads_customer_id: selectedGoogleAdsAccount,
          ga4_property_id: selectedGa4Property
        },
        {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Selección guardada");
      await fetchStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al guardar selección";
      toast.error(errorMsg);
    } finally {
      setSavingGoogleSelection(false);
    }
  };

  const handleGoogleRefresh = async () => {
    const orgId = getActiveOrgId();
    if (!orgId) {
      toast.error("No se pudo determinar la organización activa");
      return;
    }

    const token = localStorage.getItem("auth_token");

    try {
      const success = await fetchGoogleResources(token, orgId);
      if (success) {
        toast.success("Listas de Google actualizadas");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "No se pudieron actualizar las cuentas";
      toast.error(errorMsg);
    }
  };

  const handleAwinSync = async () => {
    const token = localStorage.getItem("auth_token");
    const orgId = getActiveOrgId();

    try {
      setSyncingAwin(true);
      const response = await axios.post(
        `${API}/awin/sync-transactions`,
        {},
        {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const synced = response.data?.records_synced ?? response.data?.transactions_synced ?? 0;
      toast.success(`Sincronización Awin completada (${synced} registros)`);
      await fetchStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al sincronizar Awin";
      toast.error(errorMsg);
    } finally {
      setSyncingAwin(false);
    }
  };

  // ---- Shopify handlers ----
  const handleShopifyOAuthInstall = async (e) => {
    e.preventDefault();
    setShopifyConnecting(true);
    const token = localStorage.getItem("auth_token");
    const orgId = getActiveOrgId();
    // Send raw input — backend normalizes all URL formats
    const shop = shopifyDomain.trim();
    try {
      const res = await axios.get(`${API}/shopify/oauth/install`, {
        params: { shop },
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { "X-Org-Id": orgId } : {}),
        }
      });
      // Redirect browser to Shopify OAuth page
      window.location.href = res.data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar conexión con Shopify");
      setShopifyConnecting(false);
    }
  };

  const handleShopifyTest = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await axios.get(`${API}/shopify/test`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success(`Conexión OK — ${res.data.shop_name} (${res.data.plan || ""})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al probar conexión");
    }
  };

  const handleShopifyManualConnect = async (e) => {
    e.preventDefault();
    setShopifyConnecting(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await axios.post(
        `${API}/shopify/connect`,
        { shop_domain: shopifyDomain, admin_api_access_token: shopifyManualToken, api_version: "2025-01" },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Shopify conectado: ${res.data.shop_name}`);
      setShopifyManualToken("");
      setShopifyManualMode(false);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al conectar Shopify");
    } finally {
      setShopifyConnecting(false);
    }
  };

  const handleShopifySyncProducts = async (fullSync) => {
    const token = localStorage.getItem("auth_token");
    setShopifySyncingProducts(true);
    try {
      await axios.post(
        `${API}/shopify/sync-products${fullSync ? "?full_sync=true" : ""}`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.info(fullSync ? "Importando todos los productos Shopify..." : "Actualizando productos nuevos...");
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/shopify/status`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
          setShopifyStatus(s.data);
          if (!s.data.products_syncing) {
            clearInterval(poll);
            setShopifySyncingProducts(false);
            if (s.data.products_error) toast.error(`Error: ${s.data.products_error}`);
            else toast.success(`Productos sincronizados: ${s.data.products_synced}`);
          }
        } catch { clearInterval(poll); setShopifySyncingProducts(false); }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar productos");
      setShopifySyncingProducts(false);
    }
  };

  const handleShopifySyncOrders = async (fullSync) => {
    const token = localStorage.getItem("auth_token");
    setShopifySyncingOrders(true);
    try {
      await axios.post(
        `${API}/shopify/sync-orders${fullSync ? "?full_sync=true" : ""}`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.info(fullSync ? "Importando pedidos Shopify (90 días)..." : "Actualizando pedidos nuevos...");
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/shopify/status`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
          setShopifyStatus(s.data);
          if (!s.data.orders_syncing) {
            clearInterval(poll);
            setShopifySyncingOrders(false);
            if (s.data.orders_error) toast.error(`Error: ${s.data.orders_error}`);
            else toast.success(`Pedidos sincronizados: ${s.data.orders_synced}`);
          }
        } catch { clearInterval(poll); setShopifySyncingOrders(false); }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar pedidos");
      setShopifySyncingOrders(false);
    }
  };

  const handleShopifyDisconnect = async () => {
    const token = localStorage.getItem("auth_token");
    const orgId = getActiveOrgId();
    try {
      await axios.delete(`${API}/shopify/disconnect`, {
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId  ? { "X-Org-Id": orgId } : {}),
        },
      });
      toast.success("Shopify desconectado y datos eliminados");
      setShopifyStatus(null);
      await fetchStatus();
    } catch (err) {
      toast.error("Error al desconectar Shopify");
    }
  };

  // Store-agnostic pattern: default margin handler — copy for each new integration
  const handleShopifyUpdateMargin = async () => {
    const token = localStorage.getItem("auth_token");
    const val = parseFloat(shopifyDefaultMargin);
    if (isNaN(val) || val < 0 || val > 100) {
      toast.error("El margen debe estar entre 0 y 100");
      return;
    }
    setShopifySavingMargin(true);
    try {
      await axios.put(
        `${API}/shopify/default-margin?margin=${val}`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Margen por defecto actualizado a ${val}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar margen");
    } finally {
      setShopifySavingMargin(false);
    }
  };

  const handleShopifyRecalculateFirstOrder = async () => {
    const token = localStorage.getItem("auth_token");
    setShopifyRecalculating(true);
    try {
      const res = await axios.post(
        `${API}/shopify/recalculate-first-order`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(res.data?.message || "Clasificación iniciada en segundo plano");
      // Poll status every 3s to update the UI
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/shopify/status`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
          setShopifyStatus(s.data);
          if (!s.data?.first_order_recalculating) {
            clearInterval(poll);
            setShopifyRecalculating(false);
            if (s.data?.first_order_recalc_error) {
              toast.error(`Error en clasificación: ${s.data.first_order_recalc_error}`);
            } else {
              toast.success("Clasificación nuevos/recurrentes completada");
            }
          }
        } catch { clearInterval(poll); setShopifyRecalculating(false); }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar clasificación");
      setShopifyRecalculating(false);
    }
  };

  const handleShopifyBackfill = async (forceRestart = false) => {
    const token = localStorage.getItem("auth_token");
    setShopifyBackfilling(true);
    try {
      const url = `${API}/shopify/backfill-orders?start_date=2020-01-01${forceRestart ? '&force_restart=true' : ''}`;
      const res = await axios.post(
        url,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(res.data?.message || "Importación histórica iniciada");
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/shopify/status`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
          setShopifyStatus(s.data);
          if (!s.data?.backfill_running) {
            clearInterval(poll);
            setShopifyBackfilling(false);
            if (s.data?.backfill_status === "failed") {
              toast.error(`Error en importación: ${s.data.backfill_error}`);
            } else if (s.data?.backfill_status === "completed") {
              toast.success(`Importación completada: ${s.data.backfill_orders_imported?.toLocaleString('es-ES')} pedidos`);
            }
          }
        } catch { clearInterval(poll); setShopifyBackfilling(false); }
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar importación histórica");
      setShopifyBackfilling(false);
    }
  };

  const handlePsConfigure = async (e) => {
    e.preventDefault();
    setPsConfigLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/prestashop/configure`,
        {
          shop_url: psConfigForm.shop_url.trim(),
          api_key: psConfigForm.api_key.trim(),
          valid_order_states: [5, 18],
          default_margin_percentage: parseFloat(psConfigForm.default_margin_percentage) || 30,
          shop_id: psConfigForm.shop_id ? parseInt(psConfigForm.shop_id) : null
        },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("PrestaShop conectado correctamente");
      setPsConfigOpen(false);
      fetchStatus();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = "Error al conectar con PrestaShop";
      if (typeof detail === 'string') errorMessage = detail;
      else if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      toast.error(errorMessage);
    } finally {
      setPsConfigLoading(false);
    }
  };

  const handleUpdatePsOrderStates = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${API}/prestashop/order-states`,
        psSelectedStates,
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Estados de pedido actualizados");
    } catch {
      toast.error("Error al actualizar estados");
    }
  };

  const handleSavePsSpecialStates = async () => {
    setPsSavingSpecialStates(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${API}/prestashop/special-states`,
        { cancelled_order_states: psCancelledStates, refund_order_states: [] },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Estados guardados. Reimporta pedidos para recalcular.");
    } catch {
      toast.error("Error al guardar estados especiales");
    } finally {
      setPsSavingSpecialStates(false);
    }
  };

  const handleUpdatePsMargin = async () => {
    const val = parseFloat(psDefaultMargin);
    if (isNaN(val) || val < 0 || val > 100) {
      toast.error("El margen debe estar entre 0 y 100");
      return;
    }
    setPsSavingMargin(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${API}/prestashop/default-margin`,
        null,
        { params: { margin: val }, withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Margen actualizado a ${val}%`);
    } catch {
      toast.error("Error al actualizar margen");
    } finally {
      setPsSavingMargin(false);
    }
  };

  const handleResetSyncStatus = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const response = await axios.post(
        `${API}/prestashop/reset-sync-status`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(response.data.message);
      await fetchStatus();
    } catch (error) {
      toast.error("Error al desbloquear sincronización");
    }
  };

  const startProductSync = async (token, fullSync) => {
    try {
      await axios.post(
        `${API}/prestashop/sync-products${fullSync ? "?full_sync=true" : ""}`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (!detail.toLowerCase().includes("sincronización")) {
        throw error;
      }
      toast.info("Sincronización de productos ya en curso. Revisando estado...");
    }

    return new Promise((resolve) => {
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(`${API}/prestashop/product-costs-status`, {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });

          const status = statusResponse.data;
          setProductCostsStatus(status);

          if (status.status === "syncing") {
            setSyncProgress({
              progress: status.progress || 0,
              page: status.current_page || 0
            });
            return;
          }

          clearInterval(intervalId);
          resolve(status);
        } catch (err) {
          clearInterval(intervalId);
          resolve({ status: "failed", error: "No se pudo obtener el estado de productos" });
        }
      };

      const intervalId = setInterval(pollStatus, 2000);
      pollStatus();
    });
  };

  const startOrdersSync = async (token, fullSync) => {
    toast.info(fullSync ? "Sincronizando pedidos (masivo)..." : "Sincronizando pedidos nuevos...");

    try {
      await axios.post(
        `${API}/prestashop/sync-orders-incremental${fullSync ? "?full_sync=true" : ""}`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (!detail.toLowerCase().includes("sincronización")) {
        throw error;
      }
      toast.info("Sincronización de pedidos ya en curso. Revisando estado...");
    }

    return new Promise((resolve) => {
      const pollStatus = async () => {
        try {
          const statusResp = await axios.get(`${API}/prestashop/orders-sync-status`, {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });

          setOrdersSyncStatus(statusResp.data);

          if (statusResp.data.status === "syncing") {
            return;
          }

          clearInterval(intervalId);
          resolve(statusResp.data);
        } catch (err) {
          clearInterval(intervalId);
          resolve({ status: "failed", last_error: "No se pudo obtener el estado de pedidos" });
        }
      };

      const intervalId = setInterval(pollStatus, 5000);
      pollStatus();
    });
  };

  // ---- Independent Products Sync ----
  const handleProductsSync = async (fullSync) => {
    setSyncingProducts(true);
    setSyncProgress({ progress: 0, page: 0 });
    const token = localStorage.getItem("auth_token");
    try {
      toast.info(fullSync ? "Importando productos y combinaciones..." : "Actualizando productos nuevos...");
      const productStatus = await startProductSync(token, fullSync);
      if (productStatus?.status === "completed") {
        toast.success(`Productos sincronizados: ${productStatus.total_products || 0}`);
      } else {
        toast.error(productStatus?.error || "Error al sincronizar productos");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sincronización de productos");
    } finally {
      setSyncProgress(null);
      setSyncingProducts(false);
      await fetchStatus();
    }
  };

  // ---- Independent Orders Sync ----
  const handleOrdersSync = async (fullSync) => {
    setSyncingOrders(true);
    const token = localStorage.getItem("auth_token");
    try {
      const ordersStatus = await startOrdersSync(token, fullSync);
      if (ordersStatus?.status === "completed") {
        toast.success(`Pedidos sincronizados: ${ordersStatus.orders_processed || 0}`);
      } else if (ordersStatus?.status === "failed") {
        toast.error(ordersStatus.last_error || "Error al sincronizar pedidos");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sincronización de pedidos");
    } finally {
      setSyncingOrders(false);
      await fetchStatus();
    }
  };

  // ---- Backfill histórico de clientes (nuevos vs recurrentes) ----
  const pollBackfillStatus = async (token) => {
    return new Promise((resolve) => {
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/prestashop/orders-sync-status`, {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          const bf = res.data?.backfill_customers;
          setOrdersSyncStatus(res.data);
          if (bf?.status === "completed") {
            clearInterval(poll);
            setBackfillingCustomers(false);
            toast.success(`Historial clasificado: ${bf.orders_processed?.toLocaleString('es-ES')} pedidos procesados.`);
            resolve("completed");
          } else if (bf?.status === "failed" || bf?.status === "cancelled") {
            clearInterval(poll);
            setBackfillingCustomers(false);
            if (bf?.status === "failed") {
              toast.error(`Error en clasificación: ${bf.error || "Error desconocido"}`);
            }
            resolve(bf?.status || "cancelled");
          }
        } catch (_) {}
      }, 5000);
    });
  };

  const handleBackfillCustomers = async () => {
    setBackfillingCustomers(true);
    const token = localStorage.getItem("auth_token");
    try {
      await axios.post(
        `${API}/prestashop/backfill-customer-orders?start_date=2020-01-01`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Importación histórica + clasificación iniciada. Puedes navegar, continúa en segundo plano.", { duration: 6000 });
      await pollBackfillStatus(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar clasificación de clientes");
      setBackfillingCustomers(false);
    }
  };

  const handleResumeBackfill = async () => {
    setBackfillingCustomers(true);
    const token = localStorage.getItem("auth_token");
    try {
      await axios.post(
        `${API}/prestashop/resume-backfill`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Reanudación iniciada desde la última página fallida.", { duration: 6000 });
      await pollBackfillStatus(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reanudar clasificación de clientes");
      setBackfillingCustomers(false);
    }
  };

  const handleCancelBackfill = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      await axios.post(
        `${API}/prestashop/cancel-backfill`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setBackfillingCustomers(false);
      setOrdersSyncStatus(prev => prev ? { ...prev, backfill_customers: { ...prev.backfill_customers, status: "cancelled" } } : prev);
      toast.info("Clasificación cancelada.");
    } catch (e) {
      toast.error("Error al cancelar");
    }
  };

  // ---- Canales de venta Shopify ----
  const fetchShopifyChannels = async () => {
    const token = localStorage.getItem("auth_token");
    setShopifyChannelsLoading(true);
    try {
      const res = await axios.get(`${API}/shopify/sales-channels`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setShopifyChannels(res.data?.channels || []);
      setShopifyActiveChannels(res.data?.active_channels || []);
    } catch (_) {}
    finally { setShopifyChannelsLoading(false); }
  };

  const handleToggleChannel = (ch) => {
    setShopifyActiveChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    );
  };

  const handleSaveChannels = async () => {
    const token = localStorage.getItem("auth_token");
    setShopifyChannelsSaving(true);
    try {
      const res = await axios.post(
        `${API}/shopify/update-sales-channels`,
        { active_sales_channels: shopifyActiveChannels },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (res.data?.rebuilding) {
        toast.success("Canales guardados. Recalculando métricas del dashboard... (puede tardar 1-2 min)", { duration: 8000 });
        // Poll until rebuild is done
        const poll = setInterval(async () => {
          try {
            const s = await axios.get(`${API}/shopify/status`, {
              withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            setShopifyStatus(s.data);
            if (!s.data?.channel_filter_rebuilding) {
              clearInterval(poll);
              toast.success("Métricas recalculadas. Recarga el dashboard para ver los datos filtrados.");
            }
          } catch { clearInterval(poll); }
        }, 5000);
      } else {
        toast.success(shopifyActiveChannels.length === 0
          ? "Filtro eliminado: se incluirán todos los canales."
          : `Canales activos guardados: ${shopifyActiveChannels.join(', ')}`
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar canales");
    } finally {
      setShopifyChannelsSaving(false);
    }
  };

  const handleBackfillChannels = async () => {
    const token = localStorage.getItem("auth_token");
    setChannelBackfillRunning(true);
    try {
      await axios.post(
        `${API}/shopify/backfill-channels`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Sincronización de canales iniciada. Puede tardar unos minutos.", { duration: 6000 });
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/shopify/backfill-channels/status`, {
            withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          setChannelBackfillStatus(res.data);
          if (res.data?.status === "completed") {
            clearInterval(poll);
            setChannelBackfillRunning(false);
            toast.success(`Canales sincronizados: ${res.data.count?.toLocaleString('es-ES')} pedidos actualizados.`);
            await fetchShopifyChannels(); // refresh the channel list
          } else if (res.data?.status === "failed") {
            clearInterval(poll);
            setChannelBackfillRunning(false);
            toast.error(`Error al sincronizar canales: ${res.data.error || "Error desconocido"}`);
          }
        } catch { clearInterval(poll); setChannelBackfillRunning(false); }
      }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar sincronización de canales");
      setChannelBackfillRunning(false);
    }
  };

  // ---- Reparar todos los costes históricos ----
  const fetchRepairAllCostsStatus = async (token) => {
    try {
      const res = await axios.get(`${API}/admin/repair-all-costs/status`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRepairAllCostsStatus(res.data);
      return res.data?.status;
    } catch (_) {
      return null;
    }
  };

  const handleRepairAllCosts = async () => {
    setRepairingAllCosts(true);
    const token = localStorage.getItem("auth_token");
    try {
      await axios.post(
        `${API}/admin/repair-all-costs`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Reparación de costes iniciada. Puede tardar varios minutos.", { duration: 6000 });
      const poll = setInterval(async () => {
        const status = await fetchRepairAllCostsStatus(token);
        if (status === "completed") {
          clearInterval(poll);
          setRepairingAllCosts(false);
          const s = repairAllCostsStatus;
          toast.success(`Reparación completada.`);
          // re-fetch to get final counts
          await fetchRepairAllCostsStatus(token);
        } else if (status === "failed") {
          clearInterval(poll);
          setRepairingAllCosts(false);
        }
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar reparación de costes");
      setRepairingAllCosts(false);
    }
  };

  // ---- Auto-sync config save (store-agnostic) ----
  const handleSaveAutoSync = async (storeType, enabled, intervalMinutes) => {
    setSavingAutoSync(true);
    const token = localStorage.getItem("auth_token");
    const orgId = getActiveOrgId();
    try {
      const res = await axios.put(
        `${API}/auto-sync/config/${storeType}`,
        { enabled, interval_minutes: intervalMinutes },
        {
          params: { organization_id: orgId },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      setAutoSyncNew(prev => ({
        ...prev,
        configs: {
          ...prev.configs,
          [storeType]: {
            ...(prev.configs[storeType] || {}),
            enabled,
            interval_minutes: intervalMinutes,
            next_run_at: res.data.next_run_at,
          }
        }
      }));
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar configuración de auto-sync");
    } finally {
      setSavingAutoSync(false);
    }
  };

  // ---- Fetch auto-sync execution logs ----
  const fetchAutoSyncLogs = async () => {
    setLoadingLogs(true);
    const token = localStorage.getItem("auth_token");
    const orgId = getActiveOrgId();
    try {
      const res = await axios.get(`${API}/auto-sync/logs?limit=10`, {
        params: { organization_id: orgId },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAutoSyncLogs(res.data.logs || []);
    } catch {
      // silently fail — logs are optional
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleAccountReset = async () => {
    const confirmValue = resetConfirmText.trim().toUpperCase();
    if (confirmValue !== "REINICIAR") {
      toast.error("Escribe REINICIAR para confirmar");
      return;
    }

    setResettingAccount(true);
    const token = localStorage.getItem("auth_token");

    try {
      await axios.post(
        `${API}/account/reset`,
        { confirmation: "CONFIRMAR_RESET" },
        {
          withCredentials: true,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(getActiveOrgId() ? { "X-Org-Id": getActiveOrgId() } : {}),
          }
        }
      );
      toast.success("Cuenta reiniciada correctamente");
      setResetModalOpen(false);
      setResetConfirmText("");
      await fetchStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al reiniciar la cuenta";
      toast.error(errorMsg);
    } finally {
      setResettingAccount(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Nunca";
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    const diffSecs = Math.floor(diffMs / 1000);
    const mins = Math.floor(diffSecs / 60);
    const secs = diffSecs % 60;
    
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="integrations-page">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">Integraciones</h1>
          <p className="text-slate-600">Conecta tus plataformas de publicidad y analítica</p>
        </div>

        {/* Member role banner — read-only view */}
        {!isOwner && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3 text-sm text-amber-800" data-testid="integrations-member-banner">
            <Shield className="w-4 h-4 shrink-0" />
            <span>Eres miembro de esta organización. Solo el propietario puede conectar o desconectar integraciones.</span>
          </div>
        )}

        {/* Status Summary */}
        <Card className="bg-slate-50 border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {summaryPlatforms.map((platform) => (
                  <div
                    key={platform.key}
                    className={`w-8 h-8 rounded-full border-2 border-white ${platform.color} flex items-center justify-center`}
                  >
                    {platform.connected ? (
                      <CheckCircle2 className="w-4 h-4 text-[#0A0A0A]" />
                    ) : (
                      <XCircle className="w-4 h-4 text-[#0A0A0A]/60" />
                    )}
                  </div>
                ))}
              </div>
              <span className="text-sm text-slate-600">
                {connectedCount} de {summaryPlatforms.length} plataformas conectadas
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Integration Cards */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <Card className="bg-white border border-slate-200" data-testid="google-oauth-card">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                      <Plug className="w-6 h-6 text-[#0A0A0A]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">Google Ads + GA4 (OAuth)</h3>
                      <p className="text-sm text-[#AAAAAA] mt-1">Una sola conexión para Ads y Analytics.</p>
                    </div>
                  </div>

                  {googleStatus.connected ? (
                    <Badge className="bg-green-100 text-green-700" data-testid="google-connected-badge">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Conectado
                    </Badge>
                  ) : statusErrors.google ? (
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 border border-amber-200" data-testid="google-check-error-badge">
                      <AlertCircle className="w-3 h-3 mr-1" /> No se pudo comprobar
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-600" data-testid="google-disconnected-badge">
                      <XCircle className="w-3 h-3 mr-1" /> No conectado
                    </Badge>
                  )}
                </div>

                {googleStatus.connected ? (
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-sm" data-testid="google-connection-info">
                      <p className="text-slate-600">
                        <span className="font-medium">Estado:</span> {googleStatus.status}
                      </p>
                      {googleStatus.auth_type && (
                        <p className="text-slate-600">
                          <span className="font-medium">Autenticación:</span>{" "}
                          {googleStatus.auth_type === "oauth" ? "OAuth" : "Service Account"}
                        </p>
                      )}
                      {googleStatus.google_ads_customer_id && (
                        <p className="text-slate-600" data-testid="google-ads-customer-id">
                          <span className="font-medium">Customer ID:</span> {googleStatus.google_ads_customer_id}
                        </p>
                      )}
                      {googleStatus.ga4_property_id && (
                        <p className="text-slate-600" data-testid="google-ga4-property-id">
                          <span className="font-medium">GA4 Property:</span> {googleStatus.ga4_property_id}
                        </p>
                      )}
                      {googleStatus.last_updated && (
                        <p className="text-xs text-[#AAAAAA]" data-testid="google-last-updated">
                          Última actualización: {formatDate(googleStatus.last_updated)}
                        </p>
                      )}
                    </div>

                    {/* Account/property selectors — only for OAuth connections */}
                    {googleStatus.auth_type === "oauth" && (
                      <>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Cuenta de Google Ads</Label>
                            <select
                              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                              value={selectedGoogleAdsAccount}
                              onChange={(e) => setSelectedGoogleAdsAccount(e.target.value)}
                              data-testid="google-ads-account-select"
                            >
                              <option value="">Selecciona una cuenta</option>
                              {selectedGoogleAdsAccount && !googleAdsAccounts.includes(selectedGoogleAdsAccount) && (
                                <option value={selectedGoogleAdsAccount}>{selectedGoogleAdsAccount} (actual)</option>
                              )}
                              {googleAdsAccounts.map((account) => (
                                <option key={account} value={account}>{account}</option>
                              ))}
                            </select>
                          </div>

                          <div className="space-y-2">
                            <Label>Propiedad GA4</Label>
                            <select
                              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                              value={selectedGa4Property}
                              onChange={(e) => setSelectedGa4Property(e.target.value)}
                              data-testid="ga4-property-select"
                            >
                              <option value="">Selecciona una propiedad</option>
                              {selectedGa4Property && !ga4Properties.find((prop) => prop.property_id === selectedGa4Property) && (
                                <option value={selectedGa4Property}>{selectedGa4Property} (actual)</option>
                              )}
                              {ga4Properties.map((property) => (
                                <option key={property.property_id} value={property.property_id}>
                                  {property.display_name || property.property_id}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {loadingGoogleResources && (
                          <p className="text-xs text-[#AAAAAA]" data-testid="google-resources-loading">
                            Cargando cuentas disponibles...
                          </p>
                        )}
                      </>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                      {googleStatus.auth_type === "oauth" && (
                        <>
                          <Button
                            className="bg-[#C8192C] hover:bg-[#A0141F] text-white"
                            onClick={handleGoogleSaveSelection}
                            disabled={savingGoogleSelection || !selectedGoogleAdsAccount || !selectedGa4Property}
                            data-testid="google-selection-save-button"
                          >
                            {savingGoogleSelection ? "Guardando..." : "Guardar selección"}
                          </Button>
                          <Button
                            variant="outline"
                            className="border-slate-200 text-slate-700"
                            onClick={handleGoogleRefresh}
                            disabled={loadingGoogleResources}
                            data-testid="google-refresh-accounts-button"
                          >
                            {loadingGoogleResources ? "Actualizando..." : "Actualizar cuentas"}
                          </Button>
                        </>
                      )}
                      {googleStatus.auth_type === "service_account" && (
                        <Button
                          className="bg-[#C8192C] hover:bg-[#A0141F] text-white"
                          onClick={handleGoogleConnect}
                          data-testid="google-upgrade-oauth-button"
                        >
                          <Plug className="w-4 h-4 mr-2" />
                          Migrar a OAuth
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="border-slate-200 text-slate-700"
                        onClick={handleGoogleDisconnect}
                        disabled={disconnectingGoogle}
                        data-testid="google-disconnect-button"
                      >
                        {disconnectingGoogle ? "Desconectando..." : "Desconectar"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                      <p className="font-medium mb-2">Conexión única Google</p>
                      <ol className="list-decimal list-inside space-y-1 text-blue-700">
                        <li>Haz clic en “Conectar con Google”.</li>
                        <li>Acepta permisos de Ads y Analytics en una sola fase.</li>
                        <li>Selecciona la cuenta de Ads y la propiedad GA4.</li>
                      </ol>
                    </div>
                    <Button
                      className="bg-[#C8192C] hover:bg-[#A0141F] text-white"
                      onClick={handleGoogleConnect}
                      data-testid="google-oauth-connect-button"
                    >
                      <Plug className="w-4 h-4 mr-2" />
                      Conectar con Google
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(platformConfig).map(([platform, config]) => (
                <IntegrationCard
                  key={platform}
                  platform={platform}
                  config={config}
                  connected={connectionStatus[platform]?.connected}
                  connectionInfo={connectionStatus[platform]}
                  onRefresh={fetchStatus}
                  onAwinSync={handleAwinSync}
                  syncingAwin={syncingAwin}
                  organizationId={getActiveOrgId()}
                  checkError={!!statusErrors[platform === "meta_ads" ? "meta_ads" : platform === "awin" ? "awin" : platform]}
                />
              ))}
            </div>
          </div>
        )}

        {/* Help Section */}
        <Collapsible>
          <Card className="bg-blue-50 border border-blue-200">
            <CollapsibleTrigger asChild>
              <CardContent className="p-4 cursor-pointer" data-testid="google-oauth-help-toggle">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileJson className="w-5 h-5 text-blue-600" />
                    <div>
                      <h4 className="font-medium text-blue-900">¿Cómo conectar Google con OAuth?</h4>
                      <p className="text-sm text-blue-700">
                        Pasos para Ads + GA4 en una sola autorización
                      </p>
                    </div>
                  </div>
                  <ChevronDown className="w-5 h-5 text-blue-600" />
                </div>
              </CardContent>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 px-4 pb-4">
                <div className="bg-white rounded-lg p-4 mt-2 border border-blue-100 space-y-4">
                  <div>
                    <h5 className="font-medium text-slate-900 mb-2">1. Verifica accesos</h5>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                      <li>Confirma que tu usuario tiene acceso a la cuenta de Google Ads</li>
                      <li>Confirma que tu usuario tiene acceso de lectura a la propiedad GA4</li>
                    </ol>
                  </div>

                  <div>
                    <h5 className="font-medium text-slate-900 mb-2">2. Conecta con Google</h5>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                      <li>Haz clic en “Conectar con Google”</li>
                      <li>Acepta los permisos de Ads y Analytics en una sola autorización</li>
                    </ol>
                  </div>

                  <div>
                    <h5 className="font-medium text-slate-900 mb-2">3. Selecciona recursos</h5>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                      <li>Elige la cuenta de Ads y la propiedad GA4</li>
                      <li>Guarda la selección para activar métricas</li>
                    </ol>
                  </div>

                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-sm text-slate-600">
                      Si no aparecen cuentas, revisa permisos en Google Ads y GA4 o reconecta la autorización.
                    </p>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Shopify Card */}
        <Card className="bg-white border border-[#E8E8E5]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-6 h-6 text-[#0A0A0A]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold">Shopify — Sincronización de Datos</h3>
                  {shopifyStatus?.configured && (
                    <Badge className="bg-[#1A7A3C] text-white text-xs">Conectado</Badge>
                  )}
                </div>
                <p className="text-[#404040] mb-4 text-sm">
                  Conecta tu tienda Shopify para sincronizar productos, pedidos, costes y devoluciones.
                </p>

                {!shopifyStatus?.configured && (
                  <div className="space-y-3">
                    {/* Mode tabs */}
                    <div className="flex gap-1 bg-[#F7F6F3] rounded-lg p-1">
                      <button
                        type="button"
                        onClick={() => setShopifyManualMode(false)}
                        className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${!shopifyManualMode ? 'bg-[#1A7A3C] text-white' : 'text-[#888888] hover:text-[#0A0A0A]'}`}
                        data-testid="shopify-tab-oauth"
                      >
                        OAuth (recomendado)
                      </button>
                      <button
                        type="button"
                        onClick={() => setShopifyManualMode(true)}
                        className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${shopifyManualMode ? 'bg-[#0A0A0A] text-white' : 'text-[#888888] hover:text-[#0A0A0A]'}`}
                        data-testid="shopify-tab-manual"
                      >
                        Token manual
                      </button>
                    </div>

                    {/* OAuth mode */}
                    {!shopifyManualMode && (
                      <form onSubmit={handleShopifyOAuthInstall} className="space-y-3">
                        <div>
                          <Label className="text-[#404040] text-xs mb-1 block">URL o nombre de tu tienda Shopify</Label>
                          <Input
                            placeholder="mitienda.myshopify.com o https://mitienda.myshopify.com"
                            value={shopifyDomain}
                            onChange={e => setShopifyDomain(e.target.value)}
                            required
                            className="bg-[#F7F6F3] border-[#E8E8E5] text-[#0A0A0A] placeholder:text-[#AAAAAA]"
                            data-testid="shopify-domain-input"
                          />
                          <p className="text-xs text-[#AAAAAA] mt-1">
                            También acepta: <code className="bg-[#F7F6F3] px-1 rounded">mitienda</code> o <code className="bg-[#F7F6F3] px-1 rounded">https://admin.shopify.com/store/mitienda</code>
                          </p>
                        </div>
                        <Button
                          type="submit"
                          disabled={shopifyConnecting || !shopifyDomain.trim()}
                          className="w-full bg-[#1A7A3C] hover:bg-[#145C2E] text-white"
                          data-testid="shopify-connect-btn"
                        >
                          {shopifyConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                          {shopifyConnecting ? "Redirigiendo a Shopify..." : "Conectar con Shopify"}
                        </Button>
                        <p className="text-xs text-[#AAAAAA] text-center">
                          Se abrirá Shopify para autorizar. Scopes: <code className="bg-[#F7F6F3] px-1 rounded">read_orders, read_products, read_inventory</code>
                        </p>
                      </form>
                    )}

                    {/* Manual token mode */}
                    {shopifyManualMode && (
                      <form onSubmit={handleShopifyManualConnect} className="space-y-3">
                        <div>
                          <Label className="text-[#404040] text-xs mb-1 block">Shop domain</Label>
                          <Input
                            placeholder="mitienda.myshopify.com"
                            value={shopifyDomain}
                            onChange={e => setShopifyDomain(e.target.value)}
                            required
                            className="bg-[#F7F6F3] border-[#E8E8E5] text-[#0A0A0A] placeholder:text-[#AAAAAA]"
                            data-testid="shopify-manual-domain-input"
                          />
                        </div>
                        <div>
                          <Label className="text-[#404040] text-xs mb-1 block">Admin API access token</Label>
                          <Input
                            type="password"
                            placeholder="shpat_..."
                            value={shopifyManualToken}
                            onChange={e => setShopifyManualToken(e.target.value)}
                            required
                            className="bg-[#F7F6F3] border-[#E8E8E5] text-[#0A0A0A] placeholder:text-[#AAAAAA]"
                            data-testid="shopify-manual-token-input"
                          />
                          <p className="text-xs text-[#AAAAAA] mt-1">
                            Shopify Admin → Settings → Apps → Develop apps → Create an app → Install → copiar token
                          </p>
                        </div>
                        <Button
                          type="submit"
                          disabled={shopifyConnecting || !shopifyDomain.trim() || !shopifyManualToken.trim()}
                          className="w-full bg-[#0A0A0A] hover:bg-[#404040] text-white"
                          data-testid="shopify-manual-connect-submit"
                        >
                          {shopifyConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                          {shopifyConnecting ? "Conectando..." : "Guardar token"}
                        </Button>
                      </form>
                    )}
                  </div>
                )}

                {shopifyStatus?.configured && (
                  <div className="space-y-3">
                    {/* Connection info */}
                    <div className="bg-[#F7F6F3] rounded-lg p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-[#404040]">Tienda:</span>
                        <span className="text-sm font-medium text-[#0A0A0A]">{shopifyStatus.shop_name || shopifyStatus.shop_domain}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#404040]">Dominio:</span>
                        <span className="text-xs text-[#888888]">{shopifyStatus.myshopify_domain || shopifyStatus.shop_domain}</span>
                      </div>
                    </div>

                    {/* Products block */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="w-4 h-4 text-blue-300" />
                        <span className="text-sm font-semibold text-[#0A0A0A]">Productos & Variantes</span>
                        {shopifyStatus.products_synced > 0 && (
                          <Badge className="ml-auto bg-blue-500/30 text-blue-200 text-xs border-blue-400/30">
                            {shopifyStatus.products_synced?.toLocaleString('es-ES')} variantes
                          </Badge>
                        )}
                      </div>
                      {shopifyStatus.last_sync_products && (
                        <p className="text-xs text-[#888888] mb-3">
                          Última sync: {new Date(shopifyStatus.last_sync_products).toLocaleString('es-ES')}
                        </p>
                      )}
                      {(shopifySyncingProducts || shopifyStatus.products_syncing) && (
                        <div className="mb-3">
                          <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden mb-1">
                            <div className="bg-blue-400 h-full animate-none w-full" />
                          </div>
                          <p className="text-xs text-[#888888]">Sincronizando... puedes cerrar esta página.</p>
                        </div>
                      )}
                      {shopifyStatus.products_error && (
                        <p className="text-xs text-red-300 mb-2" data-testid="shopify-products-error">Error: {shopifyStatus.products_error}</p>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <Button variant="secondary" size="sm"
                          className="bg-white/20 hover:bg-white/30 text-[#0A0A0A] border-[#E8E8E5] text-xs"
                          onClick={() => handleShopifySyncProducts(true)}
                          disabled={shopifySyncingProducts || shopifyStatus.products_syncing}
                          data-testid="shopify-products-import-all"
                        >
                          {shopifySyncingProducts ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                          Importar todo
                        </Button>
                        <Button variant="outline" size="sm"
                          className="bg-transparent hover:bg-[#F7F6F3] text-[#0A0A0A] border-white/40 text-xs"
                          onClick={() => handleShopifySyncProducts(false)}
                          disabled={shopifySyncingProducts || shopifyStatus.products_syncing}
                          data-testid="shopify-products-update-new"
                        >
                          {shopifySyncingProducts ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Actualizar nuevos
                        </Button>
                      </div>
                    </div>

                    {/* Orders block */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-3">
                        <ShoppingCart className="w-4 h-4 text-green-300" />
                        <span className="text-sm font-semibold text-[#0A0A0A]">Pedidos & Costes</span>
                        {shopifyStatus.orders_synced > 0 && (
                          <Badge className="ml-auto bg-green-500/30 text-green-200 text-xs border-green-400/30">
                            {shopifyStatus.orders_synced?.toLocaleString('es-ES')} pedidos
                          </Badge>
                        )}
                      </div>
                      {shopifyStatus.last_sync_orders && (
                        <p className="text-xs text-[#888888] mb-3">
                          Última sync: {new Date(shopifyStatus.last_sync_orders).toLocaleString('es-ES')}
                        </p>
                      )}
                      {(shopifySyncingOrders || shopifyStatus.orders_syncing) && (
                        <div className="mb-3">
                          <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden mb-1">
                            <div className="bg-green-400 h-full animate-none w-full" />
                          </div>
                          <p className="text-xs text-[#888888]">Sincronizando... puedes cerrar esta página.</p>
                        </div>
                      )}
                      {shopifyStatus.orders_error && (
                        <p className="text-xs text-red-300 mb-2" data-testid="shopify-orders-error">Error: {shopifyStatus.orders_error}</p>
                      )}

                      <Dialog open={shopifyImportModal} onOpenChange={setShopifyImportModal}>
                        <div className="grid grid-cols-2 gap-2">
                          <DialogTrigger asChild>
                            <Button variant="secondary" size="sm"
                              className="bg-white/20 hover:bg-white/30 text-[#0A0A0A] border-[#E8E8E5] text-xs"
                              disabled={shopifySyncingOrders || shopifyStatus.orders_syncing}
                              data-testid="shopify-orders-import-all"
                            >
                              <Upload className="w-3 h-3 mr-1" /> Importar todo
                            </Button>
                          </DialogTrigger>
                          <Button variant="outline" size="sm"
                            className="bg-transparent hover:bg-[#F7F6F3] text-[#0A0A0A] border-white/40 text-xs"
                            onClick={() => handleShopifySyncOrders(false)}
                            disabled={shopifySyncingOrders || shopifyStatus.orders_syncing}
                            data-testid="shopify-orders-update-new"
                          >
                            {shopifySyncingOrders ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                            Actualizar nuevos
                          </Button>
                        </div>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Importar todos los pedidos (90 días)</DialogTitle>
                            <DialogDescription>
                              Esto borrará y reimportará los pedidos de los últimos 90 días. Las métricas se recalcularán. ¿Continuar?
                            </DialogDescription>
                          </DialogHeader>
                          <div className="flex gap-3 justify-end pt-2">
                            <Button variant="outline" onClick={() => setShopifyImportModal(false)}>Cancelar</Button>
                            <Button className="bg-[#1A7A3C] hover:bg-[#145C2E] text-white" onClick={() => { setShopifyImportModal(false); handleShopifySyncOrders(true); }} data-testid="shopify-orders-import-all-confirm">
                              Sí, importar
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Nuevos vs Recurrentes block */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-purple-300" />
                        <span className="text-sm font-semibold text-[#0A0A0A]">Nuevos vs Recurrentes</span>
                        {shopifyStatus.classified_count > 0 && (
                          <Badge className="ml-auto bg-purple-500/30 text-purple-200 text-xs border-purple-400/30">
                            {shopifyStatus.classified_count?.toLocaleString('es-ES')} clasificados
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-[#888888] mb-3">
                        {shopifyStatus.customer_id_count > 0
                          ? `${shopifyStatus.customer_id_count?.toLocaleString('es-ES')} pedidos con cliente identificado.`
                          : "Sin datos de cliente. Reimporta los pedidos con «Importar todo»."}
                      </p>
                      {shopifyStatus.first_order_recalc_error && (
                        <p className="text-xs text-red-300 mb-2">Error: {shopifyStatus.first_order_recalc_error}</p>
                      )}
                      {(shopifyRecalculating || shopifyStatus.first_order_recalculating) && (
                        <div className="mb-3">
                          <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden mb-1">
                            <div className="bg-purple-400 h-full animate-none w-full" />
                          </div>
                          <p className="text-xs text-[#888888]">Clasificando... puedes cerrar esta página.</p>
                        </div>
                      )}
                      <Button
                        variant="secondary" size="sm"
                        className="w-full bg-white/20 hover:bg-white/30 text-[#0A0A0A] border-[#E8E8E5] text-xs"
                        onClick={handleShopifyRecalculateFirstOrder}
                        disabled={shopifyRecalculating || shopifyStatus.first_order_recalculating || shopifyStatus.customer_id_count === 0}
                        data-testid="shopify-recalculate-first-order-btn"
                      >
                        {(shopifyRecalculating || shopifyStatus.first_order_recalculating)
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Clasificando...</>
                          : <><Users className="w-3 h-3 mr-1" /> Clasificar nuevos / recurrentes</>}
                      </Button>
                    </div>

                    {/* Historial completo de pedidos */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <History className="w-4 h-4 text-blue-300" />
                        <span className="text-sm font-semibold text-[#0A0A0A]">Historial completo</span>
                        {shopifyStatus.backfill_status === "completed" && (
                          <Badge className="ml-auto bg-blue-500/30 text-blue-200 text-xs border-blue-400/30">
                            {shopifyStatus.backfill_orders_imported?.toLocaleString('es-ES')} importados
                          </Badge>
                        )}
                      </div>

                      {(shopifyBackfilling || shopifyStatus.backfill_running) && (
                        <div className="mb-3">
                          <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden mb-1">
                            <div className="bg-blue-400 h-full animate-none w-full" />
                          </div>
                          <p className="text-xs text-[#888888]">
                            {shopifyStatus.backfill_orders_imported > 0
                              ? `Procesando ${shopifyStatus.backfill_current_chunk || ''}... ${shopifyStatus.backfill_orders_imported?.toLocaleString('es-ES')} pedidos importados`
                              : "Descargando pedidos de Shopify... puede tardar varios minutos."}
                          </p>
                          <button
                            className="mt-2 text-xs text-red-300 underline hover:text-red-200"
                            onClick={async () => {
                              const token = localStorage.getItem("auth_token");
                              try {
                                await axios.post(`${API}/shopify/reset-backfill`, {}, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
                                setShopifyBackfilling(false);
                                const s = await axios.get(`${API}/shopify/status`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
                                setShopifyStatus(s.data);
                                toast.success("Importación cancelada");
                              } catch { toast.error("Error al cancelar"); }
                            }}
                          >
                            Cancelar importación
                          </button>
                        </div>
                      )}
                      {!shopifyBackfilling && !shopifyStatus.backfill_running && shopifyStatus.backfill_status === "completed" && (
                        <p className="text-xs text-green-300 mb-2">
                          Historial importado: {shopifyStatus.backfill_orders_imported?.toLocaleString('es-ES')} pedidos desde {shopifyStatus.backfill_start_date}
                        </p>
                      )}
                      {!shopifyBackfilling && !shopifyStatus.backfill_running && shopifyStatus.backfill_status === "failed" && (
                        <p className="text-xs text-red-300 mb-2">Error: {shopifyStatus.backfill_error}</p>
                      )}
                      {!shopifyBackfilling && !shopifyStatus.backfill_running && !shopifyStatus.backfill_status && (
                        <p className="text-xs text-[#888888] mb-3">
                          Importa todos los pedidos históricos desde el inicio de tu tienda para tener el análisis completo de nuevos vs recurrentes.
                        </p>
                      )}

                      {/* Checkpoint info — show when there's a partial import to resume */}
                      {!shopifyBackfilling && !shopifyStatus.backfill_running && shopifyStatus.backfill_last_completed_chunk && shopifyStatus.backfill_status !== "completed" && (
                        <div className="bg-amber-500/10 border border-amber-400/30 rounded-md p-2 mb-3 text-xs text-amber-300">
                          Importación pausada en <strong>{shopifyStatus.backfill_last_completed_chunk}</strong> ({shopifyStatus.backfill_orders_imported?.toLocaleString('es-ES')} pedidos).
                          Al reanudar continuará desde ese punto.
                        </div>
                      )}

                      <div className="flex flex-col gap-1.5">
                        <Button
                          variant="outline" size="sm"
                          className="w-full bg-transparent hover:bg-blue-500/20 text-blue-300 border-blue-400/40 text-xs"
                          onClick={() => handleShopifyBackfill(false)}
                          disabled={shopifyBackfilling || shopifyStatus.backfill_running || shopifySyncingOrders || shopifyStatus.orders_syncing}
                          data-testid="shopify-backfill-orders-btn"
                        >
                          {(shopifyBackfilling || shopifyStatus.backfill_running)
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Importando historial...</>
                            : shopifyStatus.backfill_last_completed_chunk && shopifyStatus.backfill_status !== "completed"
                              ? <><History className="w-3 h-3 mr-1" />Reanudar importación</>
                              : <><History className="w-3 h-3 mr-1" />Importar historial completo</>
                          }
                        </Button>
                        {/* Restart from scratch — only show if there's a partial import */}
                        {!shopifyBackfilling && !shopifyStatus.backfill_running && shopifyStatus.backfill_last_completed_chunk && shopifyStatus.backfill_status !== "completed" && (
                          <button
                            className="text-xs text-[#AAAAAA] underline hover:text-[#404040] text-center"
                            onClick={() => handleShopifyBackfill(true)}
                            data-testid="shopify-backfill-force-restart-btn"
                          >
                            Reiniciar desde cero
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Default Margin Block — store-agnostic pattern: replicate for each new integration */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Settings className="w-4 h-4 text-amber-300" />
                        <span className="text-sm font-semibold text-[#0A0A0A]">Margen por defecto</span>
                      </div>
                      <p className="text-xs text-[#888888] mb-3">
                        Se aplica a productos sin coste configurado en Shopify. Si tu margen habitual es del 30%, pon 30.
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={shopifyDefaultMargin}
                          onChange={e => setShopifyDefaultMargin(e.target.value)}
                          className="w-20 bg-[#F7F6F3] border border-[#E8E8E5] text-[#0A0A0A] rounded-md px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-amber-400"
                          data-testid="shopify-margin-input"
                        />
                        <span className="text-[#404040] text-sm">%</span>
                        <Button
                          size="sm"
                          onClick={handleShopifyUpdateMargin}
                          disabled={shopifySavingMargin}
                          className="ml-auto bg-[#B45309] hover:bg-[#92400E] text-white text-xs"
                          data-testid="shopify-margin-save-btn"
                        >
                          {shopifySavingMargin ? <Loader2 className="w-3 h-3 animate-spin" /> : "Guardar"}
                        </Button>
                      </div>
                    </div>

                    {/* Auto-sync for Shopify */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-green-300" />
                          <span className="text-sm font-semibold text-[#0A0A0A]">Sincronización automática</span>
                        </div>
                        <button
                          onClick={() => handleSaveAutoSync(
                            "shopify",
                            !autoSyncNew.configs.shopify?.enabled,
                            autoSyncNew.configs.shopify?.interval_minutes || 1440
                          )}
                          disabled={savingAutoSync}
                          className="flex items-center gap-1.5 text-xs focus:outline-none"
                          data-testid="shopify-autosync-toggle"
                        >
                          {autoSyncNew.configs.shopify?.enabled
                            ? <ToggleRight className="w-6 h-6 text-green-400" />
                            : <ToggleLeft className="w-6 h-6 text-[#AAAAAA]" />}
                          <span className={autoSyncNew.configs.shopify?.enabled ? "text-green-300" : "text-[#888888]"}>
                            {autoSyncNew.configs.shopify?.enabled ? "Activado" : "Desactivado"}
                          </span>
                        </button>
                      </div>
                      {autoSyncNew.configs.shopify?.enabled && (
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-[#404040]">Frecuencia:</span>
                            <select
                              value={autoSyncNew.configs.shopify?.interval_minutes || 1440}
                              onChange={(e) => handleSaveAutoSync("shopify", true, parseInt(e.target.value))}
                              disabled={savingAutoSync}
                              className="bg-[#F7F6F3] border border-[#E8E8E5] text-[#0A0A0A] text-xs rounded px-2 py-1 focus:outline-none"
                              data-testid="shopify-autosync-interval"
                            >
                              {(autoSyncNew.syncOptions || [1440]).map(opt => (
                                <option key={opt} value={opt} className="bg-white">
                                  {autoSyncNew.syncLabels?.[opt] || `${opt} min`}
                                </option>
                              ))}
                            </select>
                          </div>
                          {autoSyncNew.configs.shopify?.next_run_at && (
                            <p className="text-xs text-[#888888]">
                              Próxima sync: {new Date(autoSyncNew.configs.shopify.next_run_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          )}
                          <p className="text-xs text-[#888888]">Sincronización incremental (productos + pedidos nuevos)</p>
                        </div>
                      )}
                      {!autoSyncNew.configs.shopify?.enabled && (
                        <p className="text-xs text-[#AAAAAA]">
                          Actívalo para sincronizar Shopify automáticamente según la frecuencia de tu plan.
                        </p>
                      )}
                    </div>

                    {/* Sales Channel Filter */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10" data-testid="shopify-sales-channels-block">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Filter className="w-4 h-4 text-blue-300" />
                          <span className="text-sm font-semibold text-[#0A0A0A]">Canales de venta</span>
                        </div>
                        {shopifyChannelsLoading && <Loader2 className="w-3 h-3 animate-spin text-[#888888]" />}
                      </div>
                      <p className="text-xs text-[#888888] mb-3">
                        Selecciona qué canales quieres incluir en el análisis. Deja todos sin marcar para incluir todos.
                      </p>

                      {/* Always show saved selection if active channels exist */}
                      {shopifyActiveChannels.length > 0 && shopifyChannels.length === 0 && !shopifyChannelsLoading && (
                        <div className="bg-blue-500/10 border border-blue-400/30 rounded-md p-2 mb-3 text-xs text-blue-300">
                          Filtro activo: <strong>{shopifyActiveChannels.join(', ')}</strong>
                          <br />
                          <span className="text-[#AAAAAA]">Sincroniza los canales para poder modificar la selección.</span>
                        </div>
                      )}

                      {/* Channel checkboxes — only when channel data is loaded */}
                      {shopifyChannels.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {shopifyChannels.map(ch => (
                            <label key={ch} className="flex items-center gap-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={shopifyActiveChannels.length === 0 || shopifyActiveChannels.includes(ch)}
                                onChange={() => handleToggleChannel(ch)}
                                className="w-3.5 h-3.5 accent-blue-400"
                                data-testid={`channel-checkbox-${ch.replace(/\s+/g, '-').toLowerCase()}`}
                              />
                              <span className="text-xs text-[#404040] group-hover:text-[#0A0A0A] transition-colors">{ch}</span>
                              {shopifyActiveChannels.length > 0 && !shopifyActiveChannels.includes(ch) && (
                                <span className="text-xs text-slate-600 ml-auto">excluido</span>
                              )}
                            </label>
                          ))}
                          <p className="text-xs text-slate-600 mt-1">
                            Los pedidos sin canal asignado siempre se incluyen.
                          </p>
                        </div>
                      )}

                      {/* Save button — show when channels are loaded */}
                      {shopifyChannels.length > 0 && (
                        <Button
                          size="sm" variant="outline"
                          className="w-full bg-transparent hover:bg-blue-500/20 text-blue-300 border-blue-400/40 text-xs"
                          onClick={handleSaveChannels}
                          disabled={shopifyChannelsSaving || shopifyStatus?.channel_filter_rebuilding}
                          data-testid="shopify-save-channels-btn"
                        >
                          {shopifyChannelsSaving
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Guardando...</>
                            : shopifyStatus?.channel_filter_rebuilding
                              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Recalculando métricas...</>
                              : "Guardar selección de canales"
                          }
                        </Button>
                      )}

                      {/* Sync channels button — always visible as secondary option */}
                      <div className={shopifyChannels.length > 0 ? "mt-2" : ""}>
                        {shopifyChannels.length === 0 && !shopifyChannelsLoading && (
                          <Button
                            size="sm" variant="outline"
                            className="w-full bg-transparent hover:bg-blue-500/20 text-blue-300 border-blue-400/40 text-xs"
                            onClick={handleBackfillChannels}
                            disabled={channelBackfillRunning}
                            data-testid="shopify-backfill-channels-btn"
                          >
                            {channelBackfillRunning
                              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sincronizando canales... {channelBackfillStatus?.count > 0 ? `(${channelBackfillStatus.count.toLocaleString('es-ES')} pedidos)` : ''}</>
                              : <><RefreshCw className="w-3 h-3 mr-1" />Sincronizar canales de pedidos existentes</>
                            }
                          </Button>
                        )}
                        {shopifyChannels.length > 0 && (
                          <button
                            className="text-xs text-slate-600 hover:text-[#888888] w-full text-center mt-1"
                            onClick={handleBackfillChannels}
                            disabled={channelBackfillRunning}
                            data-testid="shopify-backfill-channels-refresh-btn"
                          >
                            {channelBackfillRunning ? "Actualizando..." : "Actualizar lista de canales"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Test + Disconnect */}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm"
                        className="bg-transparent hover:bg-[#F7F6F3] text-[#0A0A0A] border-white/30 text-xs flex-1"
                        onClick={handleShopifyTest}
                        data-testid="shopify-test-btn"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Test conexión
                      </Button>
                      <Button variant="outline" size="sm"
                        className="bg-transparent hover:bg-red-500/20 text-red-300 border-red-400/30 text-xs"
                        onClick={handleShopifyDisconnect}
                        data-testid="shopify-disconnect-btn"
                      >
                        Desconectar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PrestaShop Card */}
        <Card className="bg-white border border-[#E8E8E5]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-[#0A0A0A]" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">PrestaShop - Sincronización de Datos</h3>
                <p className="text-[#404040] mb-4 text-sm">
                  Sincroniza productos, pedidos y costes para análisis de rentabilidad preciso.
                </p>
                
                {prestashopStatus?.configured && (
                  <div className="space-y-4">
                    {/* Connection Status */}
                    <div className="bg-[#F7F6F3] rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-[#404040]">Estado de conexión:</span>
                        {statusErrors.prestashop ? (
                          <Badge variant="secondary" className="bg-amber-50 text-amber-700 border border-amber-200" data-testid="prestashop-check-error-badge">
                            <AlertCircle className="w-3 h-3 mr-1" /> No se pudo comprobar
                          </Badge>
                        ) : (
                          <Badge className="bg-[#1A7A3C] text-white">
                            {prestashopStatus.connected ? "Conectado" : "Configurado"}
                          </Badge>
                        )}
                      </div>
                      {prestashopStatus.shop_url && (
                        <div className="text-sm text-[#404040]">
                          <span className="font-medium">Tienda:</span> {prestashopStatus.shop_url}
                        </div>
                      )}
                      {prestashopStatus.last_checked && (
                        <div className="text-xs text-[#888888] mt-1">
                          Última verificación: {new Date(prestashopStatus.last_checked).toLocaleString("es-ES")}
                        </div>
                      )}
                      {prestashopStatus.egress_ip && (
                        <div className="text-xs text-[#404040] mt-1 font-mono flex items-center gap-1" data-testid="prestashop-egress-ip">
                          <span className="text-[#888888]">IP de salida (producción):</span>
                          <span className="font-semibold text-[#C8192C] select-all">{prestashopStatus.egress_ip}</span>
                          <button
                            className="ml-1 text-[#888888] hover:text-[#C8192C] transition-colors"
                            title="Copiar IP"
                            onClick={() => { navigator.clipboard.writeText(prestashopStatus.egress_ip); toast.success("IP copiada"); }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                          </button>
                        </div>
                      )}
                      {isOwner && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            data-testid="prestashop-test-connection-btn"
                            onClick={async () => {
                              const token = localStorage.getItem("auth_token");
                              const headers = token ? { Authorization: `Bearer ${token}` } : {};
                              try {
                                const res = await axios.post(`${API}/prestashop/test-connection`, {}, { withCredentials: true, headers });
                                setPrestashopStatus(prev => ({ ...prev, ...res.data }));
                                setStatusErrors(prev => ({ ...prev, prestashop: false }));
                                if (res.data.connected) {
                                  const ipMsg = res.data.egress_ip ? ` · IP de salida: ${res.data.egress_ip}` : "";
                                  toast.success(`Conexión con PrestaShop verificada${ipMsg}`);
                                } else toast.error("No se pudo conectar con la tienda PrestaShop");
                              } catch {
                                toast.error("Error al probar la conexión");
                              }
                            }}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Probar conexión
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            data-testid="prestashop-edit-config-btn"
                            onClick={() => {
                              setPsConfigForm(prev => ({
                                ...prev,
                                shop_url: prestashopStatus.shop_url || "",
                                api_key: "",
                              }));
                              setPsConfigOpen(true);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" /> Editar configuración
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Sync Section — split into Products and Orders */}
                    <div className="space-y-3">

                      {/* === BLOCK 1: Products & Combinations === */}
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <div className="flex items-center gap-2 mb-3">
                          <Package className="w-4 h-4 text-blue-300" />
                          <span className="text-sm font-semibold text-[#0A0A0A]">Productos & Combinaciones</span>
                          {productCostsStatus?.synced && (
                            <Badge className="ml-auto bg-blue-500/30 text-blue-200 text-xs border-blue-400/30">
                              {productCostsStatus.total_products?.toLocaleString('es-ES')} productos
                            </Badge>
                          )}
                        </div>

                        {productCostsStatus?.last_sync && (
                          <p className="text-xs text-[#888888] mb-3">
                            Última sync: {formatDate(productCostsStatus.last_sync)}
                            {productCostsStatus?.sync_type && ` · ${productCostsStatus.sync_type}`}
                          </p>
                        )}

                        {isProductsSyncing && (
                          <div className="mb-3 space-y-1">
                            <div className="flex items-center justify-between text-xs text-[#0A0A0A]">
                              <span>
                                {productCostsStatus?.phase === 'stock'
                                  ? 'Actualizando stock...'
                                  : productCostsStatus?.phase === 'combinations'
                                    ? 'Sincronizando combinaciones...'
                                    : 'Sincronizando productos...'}
                              </span>
                              <span className="font-mono text-[#404040]">
                                {productCostsStatus?.phase === 'stock'
                                  ? (productCostsStatus?.stock_total > 0
                                      ? `${productCostsStatus.stock_progress || 0}/${productCostsStatus.stock_total} lotes`
                                      : `página ${productCostsStatus?.stock_progress || 0}`)
                                  : (productCostsStatus?.progress
                                      ? `${productCostsStatus.progress.toLocaleString()} procesados`
                                      : 'En curso...')}
                              </span>
                            </div>
                            <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                              <div
                                className={`h-full animate-none ${productCostsStatus?.phase === 'stock' ? 'bg-emerald-400' : 'bg-blue-400'}`}
                                style={{
                                  width: (productCostsStatus?.phase === 'stock' && productCostsStatus?.stock_total > 0)
                                    ? `${Math.min(100, ((productCostsStatus.stock_progress || 0) / productCostsStatus.stock_total) * 100)}%`
                                    : '100%'
                                }}
                              />
                            </div>
                            <p className="text-xs text-[#888888]">Puedes cerrar esta página, continúa en segundo plano.</p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="bg-white/20 hover:bg-white/30 text-[#0A0A0A] border-[#E8E8E5] text-xs"
                            onClick={() => handleProductsSync(true)}
                            disabled={isProductsSyncing}
                            data-testid="prestashop-products-sync-massive"
                          >
                            {isProductsSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                            Importar todo
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-transparent hover:bg-[#F7F6F3] text-[#0A0A0A] border-white/40 text-xs"
                            onClick={() => handleProductsSync(false)}
                            disabled={isProductsSyncing}
                            data-testid="prestashop-products-sync-incremental"
                          >
                            {isProductsSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                            Actualizar nuevos
                          </Button>
                        </div>

                        {productCostsStatus?.status === 'syncing' && !syncingProducts && (
                          <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-xs text-amber-200 mb-1">Sync atascada más de 20 min:</p>
                            <Button
                              variant="outline" size="sm"
                              className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border-amber-400/30 text-xs"
                              onClick={handleResetSyncStatus}
                              data-testid="prestashop-reset-sync-button"
                            >
                              <AlertCircle className="w-3 h-3 mr-1" /> Resetear
                            </Button>
                          </div>
                        )}
                        {productCostsStatus?.status === 'failed' && (
                          <div className="mt-2 p-2 bg-red-500/10 border border-red-400/30 rounded-lg" data-testid="prestashop-sync-error">
                            <p className="text-xs text-red-200">Error: {productCostsStatus?.error || "Error al sincronizar"}</p>
                          </div>
                        )}
                        {!productCostsStatus?.synced && !isProductsSyncing && (
                          <p className="text-xs text-yellow-300 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Primera vez: importa todo el catálogo
                          </p>
                        )}
                      </div>

                      {/* === BLOCK 2: Orders & Costs === */}
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <div className="flex items-center gap-2 mb-3">
                          <ShoppingCart className="w-4 h-4 text-green-300" />
                          <span className="text-sm font-semibold text-[#0A0A0A]">Pedidos & Costes</span>
                          {ordersSyncStatus?.orders_processed > 0 && (
                            <Badge className="ml-auto bg-green-500/30 text-green-200 text-xs border-green-400/30">
                              {ordersSyncStatus.orders_processed?.toLocaleString('es-ES')} pedidos
                            </Badge>
                          )}
                        </div>

                        {ordersSyncStatus?.synced_at && (
                          <p className="text-xs text-[#888888] mb-3" data-testid="prestashop-orders-last-sync">
                            Última sync: {formatDate(ordersSyncStatus.synced_at)}
                            {ordersSyncStatus?.current_sync_type && ` · ${ordersSyncStatus.current_sync_type}`}
                          </p>
                        )}

                        {isOrdersSyncing && (
                          <div className="mb-3 space-y-1">
                            <div className="flex items-center justify-between text-xs text-[#0A0A0A]">
                              <span>Sincronizando pedidos...</span>
                            </div>
                            <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                              <div className="bg-green-400 h-full animate-none" style={{width: '100%'}} />
                            </div>
                            <p className="text-xs text-[#888888]" data-testid="prestashop-orders-sync-status">Puedes cerrar esta página, continúa en segundo plano.</p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="bg-white/20 hover:bg-white/30 text-[#0A0A0A] border-[#E8E8E5] text-xs"
                            onClick={() => handleOrdersSync(true)}
                            disabled={isOrdersSyncing}
                            data-testid="prestashop-orders-sync-massive"
                          >
                            {isOrdersSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                            Importar todo
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-transparent hover:bg-[#F7F6F3] text-[#0A0A0A] border-white/40 text-xs"
                            onClick={() => handleOrdersSync(false)}
                            disabled={isOrdersSyncing}
                            data-testid="prestashop-orders-sync-incremental"
                          >
                            {isOrdersSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                            Actualizar nuevos
                          </Button>
                        </div>

                        {/* Backfill histórico nuevos vs recurrentes */}
                        <div className="mt-2 pt-2 border-t border-white/10">
                          {backfillingCustomers && (
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs text-blue-300">
                                {ordersSyncStatus?.backfill_customers?.orders_processed > 0
                                  ? `Procesando histórico... ${ordersSyncStatus.backfill_customers.orders_processed?.toLocaleString('es-ES')} pedidos (página ${ordersSyncStatus.backfill_customers.current_page || 0})`
                                  : "Clasificando historial..."}
                              </p>
                              <button
                                onClick={handleCancelBackfill}
                                className="text-xs text-red-400 hover:text-red-300 underline ml-2"
                                data-testid="cancel-backfill-btn"
                              >
                                Cancelar
                              </button>
                            </div>
                          )}
                          {!backfillingCustomers && ordersSyncStatus?.backfill_customers?.status === "completed" && (
                            <p className="text-xs text-green-300 mb-2">
                              Historial clasificado: {ordersSyncStatus.backfill_customers.orders_processed?.toLocaleString('es-ES')} pedidos
                            </p>
                          )}
                          {!backfillingCustomers && ordersSyncStatus?.backfill_customers?.status === "failed" && (
                            <div className="mb-2 p-2 rounded border border-amber-400/40 bg-amber-500/10" data-testid="prestashop-backfill-failed-state">
                              <p className="text-xs text-amber-200">
                                Backfill interrumpido en página {ordersSyncStatus?.backfill_customers?.current_page || 0}.
                                {ordersSyncStatus?.backfill_customers?.error ? ` Error: ${ordersSyncStatus.backfill_customers.error}` : ""}
                              </p>
                            </div>
                          )}
                          {!backfillingCustomers && (!ordersSyncStatus?.backfill_customers?.status || ordersSyncStatus?.backfill_customers?.status === "cancelled") && (
                            <p className="text-xs text-[#888888] mb-2">
                              Importa pedidos históricos faltantes y clasifica todo el historial para identificar clientes nuevos vs recurrentes.
                            </p>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full bg-transparent hover:bg-blue-500/20 text-blue-300 border-blue-400/40 text-xs"
                            onClick={handleBackfillCustomers}
                            disabled={isOrdersSyncing || backfillingCustomers}
                            data-testid="prestashop-backfill-customers-btn"
                          >
                            {backfillingCustomers
                              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Importando y clasificando...</>
                              : <><Users className="w-3 h-3 mr-1" />Importar + clasificar historial</>
                            }
                          </Button>
                          {canResumeBackfill && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full mt-2 bg-transparent hover:bg-amber-500/20 text-amber-200 border-amber-400/40 text-xs"
                              onClick={handleResumeBackfill}
                              disabled={isOrdersSyncing || backfillingCustomers}
                              data-testid="prestashop-resume-backfill-btn"
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />Reanudar desde última página fallida
                            </Button>
                          )}
                        </div>

                        {ordersSyncStatus?.status === 'failed' && (
                          <div className="mt-2 p-2 bg-red-500/10 border border-red-400/30 rounded-lg" data-testid="prestashop-orders-error">
                            <p className="text-xs text-red-200">Error: {ordersSyncStatus?.last_error || "Error al sincronizar pedidos"}</p>
                          </div>
                        )}
                      </div>

                      {/* === BLOCK 3: Auto-sync diario === */}
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-purple-300" />
                            <span className="text-sm font-semibold text-[#0A0A0A]">Sincronización automática</span>
                          </div>
                          <button
                            onClick={() => handleSaveAutoSync(
                              "prestashop",
                              !autoSyncNew.configs.prestashop?.enabled,
                              autoSyncNew.configs.prestashop?.interval_minutes || 1440
                            )}
                            disabled={savingAutoSync}
                            className="flex items-center gap-1.5 text-xs focus:outline-none"
                            data-testid="prestashop-autosync-toggle"
                          >
                            {autoSyncNew.configs.prestashop?.enabled
                              ? <ToggleRight className="w-6 h-6 text-green-400" />
                              : <ToggleLeft className="w-6 h-6 text-[#AAAAAA]" />}
                            <span className={autoSyncNew.configs.prestashop?.enabled ? "text-green-300" : "text-[#888888]"}>
                              {autoSyncNew.configs.prestashop?.enabled ? "Activado" : "Desactivado"}
                            </span>
                          </button>
                        </div>

                        {autoSyncNew.configs.prestashop?.enabled && (
                          <div className="space-y-2 mb-3">
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-[#404040]">Frecuencia:</span>
                              <select
                                value={autoSyncNew.configs.prestashop?.interval_minutes || 1440}
                                onChange={(e) => handleSaveAutoSync("prestashop", true, parseInt(e.target.value))}
                                disabled={savingAutoSync}
                                className="bg-[#F7F6F3] border border-[#E8E8E5] text-[#0A0A0A] text-xs rounded px-2 py-1 focus:outline-none"
                                data-testid="prestashop-autosync-interval"
                              >
                                {(autoSyncNew.syncOptions || [1440]).map(opt => (
                                  <option key={opt} value={opt} className="bg-white">
                                    {autoSyncNew.syncLabels?.[opt] || `${opt} min`}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {autoSyncNew.configs.prestashop?.next_run_at && (
                              <p className="text-xs text-[#888888]">
                                Próxima sync: {new Date(autoSyncNew.configs.prestashop.next_run_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                              </p>
                            )}
                            <p className="text-xs text-[#888888]">
                              Sincronización incremental automática (productos + pedidos nuevos)
                            </p>
                            {autoSyncNew.planMinInterval > 30 && (
                              <p className="text-xs text-amber-400">
                                Tu plan permite hasta {autoSyncNew.syncLabels?.[autoSyncNew.planMinInterval] || `cada ${autoSyncNew.planMinInterval} min`}. Actualiza tu plan para mayor frecuencia.
                              </p>
                            )}
                          </div>
                        )}
                        {!autoSyncNew.configs.prestashop?.enabled && (
                          <p className="text-xs text-[#AAAAAA] mb-3">
                            Actívalo para que el sistema actualice productos y pedidos de forma automática según la frecuencia de tu plan.
                          </p>
                        )}

                        {/* Execution log */}
                        <div className="border-t border-white/10 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-[#404040]">Últimas ejecuciones</span>
                            <button
                              onClick={fetchAutoSyncLogs}
                              disabled={loadingLogs}
                              className="text-xs text-purple-300 hover:text-purple-200 flex items-center gap-1"
                              data-testid="prestashop-refresh-logs"
                            >
                              {loadingLogs
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Clock className="w-3 h-3" />}
                              Actualizar
                            </button>
                          </div>

                          {autoSyncLogs.length === 0 ? (
                            <p className="text-xs text-[#AAAAAA] italic">
                              {loadingLogs ? "Cargando..." : "Sin ejecuciones registradas aún. El auto-sync escribe aquí cada vez que corre."}
                            </p>
                          ) : (
                            <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                              {autoSyncLogs.map((log, i) => (
                                <div
                                  key={i}
                                  className={`flex items-start justify-between rounded px-2 py-1.5 text-xs ${
                                    log.status === 'ok'
                                      ? 'bg-green-500/10 border border-green-500/20'
                                      : 'bg-red-500/10 border border-red-500/20'
                                  }`}
                                  data-testid={`autosync-log-${i}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className={log.status === 'ok' ? 'text-green-400' : 'text-red-400'}>
                                      {log.status === 'ok' ? '✓' : '✗'}
                                    </span>
                                    <div className="min-w-0">
                                      <p className="text-slate-200 font-medium">
                                        {log.store_type && <span className="text-[#888888] mr-1">[{log.store_type}]</span>}
                                        {log.started_at ? new Date(log.started_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                                      </p>
                                      {log.status === 'ok' ? (
                                        <p className="text-[#888888]">
                                          {log.products_synced > 0 && `${log.products_synced} productos`}
                                          {log.products_synced > 0 && log.orders_synced > 0 && ' · '}
                                          {log.orders_synced > 0 && `${log.orders_synced} pedidos`}
                                          {!log.products_synced && !log.orders_synced && 'Sin cambios nuevos'}
                                        </p>
                                      ) : (
                                        <p className="text-red-300 truncate max-w-xs">{log.error || 'Error desconocido'}</p>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-[#AAAAAA] flex-shrink-0 ml-2">
                                    {log.duration_s != null ? `${log.duration_s}s` : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                    </div>

                    {/* PrestaShop Advanced Settings */}
                    <Collapsible open={psSettingsOpen} onOpenChange={setPsSettingsOpen}>
                      <CollapsibleTrigger asChild>
                        <button
                          className="w-full flex items-center justify-between px-3 py-2 mt-2 rounded-lg bg-white/5 hover:bg-[#F7F6F3] border border-white/10 text-sm text-slate-200 transition-colors"
                          data-testid="prestashop-settings-toggle"
                        >
                          <span className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-[#404040]" />
                            Configuración avanzada
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${psSettingsOpen ? "rotate-180" : ""}`} />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mt-3 space-y-5 p-3 rounded-lg bg-white/5 border border-white/10">
                          {/* Default Margin */}
                          <div>
                            <p className="text-xs font-semibold text-[#404040] mb-2">Margen por defecto</p>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0" max="100" step="1"
                                value={psDefaultMargin}
                                onChange={(e) => setPsDefaultMargin(parseFloat(e.target.value) || 0)}
                                className="w-20 bg-[#F7F6F3] border border-[#E8E8E5] text-[#0A0A0A] text-sm rounded px-2 py-1 focus:outline-none"
                                data-testid="ps-default-margin-input"
                              />
                              <span className="text-sm text-[#404040]">%</span>
                              <button
                                onClick={handleUpdatePsMargin}
                                disabled={psSavingMargin}
                                className="text-xs bg-white/20 hover:bg-white/30 text-[#0A0A0A] rounded px-3 py-1 transition-colors disabled:opacity-50"
                                data-testid="ps-save-margin-btn"
                              >
                                {psSavingMargin ? "Guardando..." : "Guardar"}
                              </button>
                            </div>
                            <p className="text-xs text-[#888888] mt-1">Para productos sin precio de coste configurado</p>
                          </div>

                          {/* Valid Order States */}
                          {psOrderStates.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold text-[#404040]">Estados de pedido válidos</p>
                                <button
                                  onClick={handleUpdatePsOrderStates}
                                  className="text-xs bg-white/20 hover:bg-white/30 text-[#0A0A0A] rounded px-2 py-1 transition-colors"
                                  data-testid="ps-save-order-states-btn"
                                >
                                  Guardar
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1">
                                {psOrderStates.map((state) => (
                                  <div
                                    key={state.id}
                                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors text-xs ${
                                      psSelectedStates.includes(state.id)
                                        ? "border-[#C8192C]/60 bg-[#FEEEF0] text-[#C8192C]"
                                        : "border-white/10 text-[#888888] hover:border-[#E8E8E5]"
                                    }`}
                                    onClick={() => setPsSelectedStates(prev =>
                                      prev.includes(state.id) ? prev.filter(s => s !== state.id) : [...prev, state.id]
                                    )}
                                    data-testid={`ps-order-state-${state.id}`}
                                  >
                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: state.color }} />
                                    <span className="truncate">{state.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Cancellation States */}
                          {psOrderStates.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold text-[#404040]">Estados de cancelación</p>
                                <button
                                  onClick={handleSavePsSpecialStates}
                                  disabled={psSavingSpecialStates}
                                  className="text-xs bg-white/20 hover:bg-white/30 text-[#0A0A0A] rounded px-2 py-1 transition-colors disabled:opacity-50"
                                  data-testid="ps-save-special-states-btn"
                                >
                                  {psSavingSpecialStates ? "Guardando..." : "Guardar"}
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1">
                                {psOrderStates.map((state) => (
                                  <div
                                    key={state.id}
                                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors text-xs ${
                                      psCancelledStates.includes(state.id)
                                        ? "border-[#C8192C]/60 bg-[#FEEEF0] text-[#C8192C]"
                                        : "border-white/10 text-[#888888] hover:border-[#E8E8E5]"
                                    }`}
                                    onClick={() => setPsCancelledStates(prev =>
                                      prev.includes(state.id) ? prev.filter(s => s !== state.id) : [...prev, state.id]
                                    )}
                                    data-testid={`ps-cancel-state-${state.id}`}
                                  >
                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: state.color }} />
                                    <span className="truncate">{state.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}

                {!prestashopStatus?.configured && (
                  <Button
                    variant="secondary"
                    className="bg-white text-slate-900 hover:bg-slate-100 mt-4"
                    data-testid="prestashop-config-button"
                    onClick={() => {
                      setPsConfigForm({ shop_url: "", api_key: "", default_margin_percentage: 30, shop_id: "" });
                      setPsConfigOpen(true);
                    }}
                  >
                    Configurar PrestaShop →
                  </Button>
                )}

                {/* Config dialog — available for initial setup AND reconfigure */}
                <Dialog open={psConfigOpen} onOpenChange={setPsConfigOpen}>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-bold">
                        {prestashopStatus?.configured ? "Editar configuración PrestaShop" : "Conectar PrestaShop"}
                      </DialogTitle>
                      <DialogDescription>
                        {prestashopStatus?.configured
                          ? "Actualiza la URL o la clave API de tu tienda"
                          : "Introduce los datos de tu tienda para conectarla"}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handlePsConfigure} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>URL de tu tienda</Label>
                        <Input
                          placeholder="https://mitienda.com"
                          value={psConfigForm.shop_url}
                          onChange={(e) => setPsConfigForm({ ...psConfigForm, shop_url: e.target.value })}
                          required
                          data-testid="prestashop-url-input"
                        />
                        <p className="text-xs text-[#AAAAAA]">Sin barra final (/)</p>
                      </div>
                      <div className="space-y-2">
                        <Label>API Key de Webservice</Label>
                        <Input
                          type="password"
                          placeholder={prestashopStatus?.configured ? "Dejar vacío para mantener la clave actual" : "Tu clave de API"}
                          value={psConfigForm.api_key}
                          onChange={(e) => setPsConfigForm({ ...psConfigForm, api_key: e.target.value })}
                          required={!prestashopStatus?.configured}
                          data-testid="prestashop-apikey-input"
                        />
                        {prestashopStatus?.configured && (
                          <p className="text-xs text-[#AAAAAA]">Deja vacío para conservar la clave actual. Rellena solo si quieres cambiarla.</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Margen por defecto (%)</Label>
                        <Input
                          type="number" min="0" max="100" step="1" placeholder="30"
                          value={psConfigForm.default_margin_percentage}
                          onChange={(e) => setPsConfigForm({ ...psConfigForm, default_margin_percentage: parseFloat(e.target.value) || 30 })}
                          data-testid="prestashop-margin-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ID de Tienda (solo multitienda)</Label>
                        <Input
                          type="number" min="1" placeholder="Dejar vacío si no es multitienda"
                          value={psConfigForm.shop_id}
                          onChange={(e) => setPsConfigForm({ ...psConfigForm, shop_id: e.target.value })}
                          data-testid="prestashop-shopid-input"
                        />
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h4 className="font-medium text-amber-900 flex items-center gap-2 text-sm">
                          <AlertCircle className="w-4 h-4" /> ¿Cómo obtener la API Key?
                        </h4>
                        <ol className="text-sm text-amber-700 mt-2 space-y-1">
                          <li>1. Panel PrestaShop → Parámetros Avanzados → Webservice</li>
                          <li>2. Activa el Webservice</li>
                          <li>3. Añade clave con permisos de lectura: orders, products, order_states</li>
                        </ol>
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#C8192C] hover:bg-[#A0141F] text-white"
                        disabled={psConfigLoading}
                        data-testid="prestashop-configure-submit-btn"
                      >
                        {psConfigLoading
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                          : prestashopStatus?.configured ? "Actualizar configuración" : "Conectar PrestaShop"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-amber-200 bg-amber-50" data-testid="repair-all-costs-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-slate-900">Reparar costes históricos</h3>
                <p className="text-sm text-slate-600">
                  Recalcula retroactivamente los costes de todas las líneas de pedido donde el coste unitario
                  difiere del precio mayorista configurado. Actualiza también las métricas diarias afectadas.
                </p>
                {repairAllCostsStatus && repairAllCostsStatus.status !== "never_run" && (
                  <div className="text-xs space-y-0.5 mt-1">
                    {repairAllCostsStatus.status === "running" && (
                      <p className="text-amber-700 font-medium flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        En proceso... {(repairAllCostsStatus.lines_corrected || 0).toLocaleString("es-ES")} líneas corregidas hasta ahora.
                      </p>
                    )}
                    {repairAllCostsStatus.status === "completed" && (
                      <p className="text-green-700 font-medium">
                        Completado — {(repairAllCostsStatus.lines_corrected || 0).toLocaleString("es-ES")} líneas corregidas ·{" "}
                        {repairAllCostsStatus.dates_rebuilt} días recalculados ·{" "}
                        {(repairAllCostsStatus.products_scanned || 0).toLocaleString("es-ES")} productos analizados
                      </p>
                    )}
                    {repairAllCostsStatus.status === "failed" && (
                      <p className="text-red-600 font-medium">Error: {repairAllCostsStatus.error || "Error desconocido"}</p>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                className="w-full md:w-auto border-amber-400 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                onClick={handleRepairAllCosts}
                disabled={repairingAllCosts}
                data-testid="repair-all-costs-button"
              >
                {repairingAllCosts
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reparando costes...</>
                  : "Reparar todos los costes"
                }
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-red-200 bg-red-50" data-testid="reset-account-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-slate-900">Reiniciar cuenta (organización activa)</h3>
                <p className="text-sm text-slate-600">
                  Elimina todas las integraciones, pedidos, productos, costes, métricas y alertas de la organización activa.
                  No elimina tu usuario ni otras organizaciones.
                </p>
                <ul className="text-xs text-[#AAAAAA] space-y-1">
                  <li>• Integraciones y tokens OAuth</li>
                  <li>• Pedidos, productos, costes y métricas agregadas</li>
                  <li>• Alertas y eventos de tracking</li>
                </ul>
              </div>

              <Dialog open={resetModalOpen} onOpenChange={(open) => {
                setResetModalOpen(open);
                if (!open) {
                  setResetConfirmText("");
                }
              }}>
                <DialogTrigger asChild>
                  <Button variant="destructive" className="w-full md:w-auto" data-testid="reset-account-button">
                    Reiniciar cuenta
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg" data-testid="reset-account-modal">
                  <DialogHeader>
                    <DialogTitle>Confirmar reinicio</DialogTitle>
                    <DialogDescription>
                      Esta acción eliminará todos los datos de la organización activa y no se puede deshacer.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="reset-account-warning">
                      Asegúrate de haber exportado los datos necesarios antes de continuar.
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reset-confirm">Escribe REINICIAR para confirmar</Label>
                      <Input
                        id="reset-confirm"
                        value={resetConfirmText}
                        onChange={(e) => setResetConfirmText(e.target.value)}
                        placeholder="REINICIAR"
                        data-testid="reset-account-input"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setResetModalOpen(false)}
                      data-testid="reset-account-cancel-button"
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleAccountReset}
                      disabled={resettingAccount || resetConfirmText.trim().toUpperCase() !== "REINICIAR"}
                      data-testid="reset-account-confirm-button"
                    >
                      {resettingAccount ? "Reiniciando..." : "Reiniciar ahora"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Integrations;
;
